// Duplicate an app, the paused-operation card and the data-shape cards (DES1-specs "Specs for
// T2-UI1: duplicate app, resume card, data-shape cards").
//
// Namespace `app-operations`; owner: T2-UI1 (ui/workspace/apps/lifecycle). Counts, sizes, digests
// and limits come from the domain; no number or limit is invented here. `{app}` an app name,
// `{n}` / `{m}` / `{count}` numbers, `{system}` a connection, `{time}` a formatted time,
// `{names}` an already-joined list. Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  close: 'Close',
  cancel: 'Cancel',
  dupTitle: 'Duplicate {app}',
  dupName: 'Name',
  dupNameDefault: '{app} copy',
  dupWhat: 'What comes with it',
  dupCodeKey: 'Code and settings',
  dupCodeValue: 'Everything in v{n}, as a new private draft',
  dupWhoKey: 'Who can open it',
  dupWhoValue: 'Only you, until you publish it',
  dupConnKey: 'Connections',
  dupConnValue: 'Not copied. It asks to read {systems} again when it first needs them',
  dupHistoryKey: 'History',
  dupHistoryValue: 'Row history, versions and restore points stay with {app}',
  dupEntries: 'Entries',
  dupEmpty: 'Start empty',
  dupEmptyHint: 'Same tables and fields, no entries.',
  dupCopy: 'Copy the entries as they are now',
  dupCopyPreview: '{tables} tables · {entries} entries · {files} files · as of {time}',
  dupCopyUpdated: 'Updated: {entries} entries now',
  dupCopyLater: 'Later changes to {app} don’t reach the copy.',
  dupCopyUnavailable: 'Copying entries isn’t available for this app: {reason}.',
  dupConfirm: 'Duplicate',
  dupBusy: 'Duplicating…',
  dupDone: 'Made {name}. Only you can open it.',
  dupUndo: 'Undo',
  dupFailed: 'Couldn’t copy the entries. Nothing was made.',
  dupUncertain:
    'We don’t know whether the copy was made. Check before trying again, so you don’t get two.',
  tryAgain: 'Try again',
  pausedPill: 'Paused',
  stepOf: 'Step {n} of {m}',
  nothingTwice: 'Nothing is done twice. It picks up from {step}.',
  approvalBody: 'Waiting for your OK to {operation}.',
  approvalOthers: 'Waiting for {names}.',
  budgetBody:
    'Paused at the budget: {spent} of {limit}. The last working version stays as a draft.',
  providerBody: '{system} didn’t answer (tried {n} times).',
  viewerBody:
    '{run} skipped {part}: it reads each person’s own {system}, and this run has no person to read as.',
  unknownBody: 'We don’t know whether {operation} went through. {system} didn’t confirm it.',
  review: 'Review',
  stopHere: 'Stop here',
  stopConfirm: 'Stop here? The last working version stays as a draft.',
  stopYes: 'Stop',
  stopKeep: 'Keep going',
  continueMore: 'Continue with {amount} more',
  checkAgain: 'Check again',
  chooseRunAs: 'Choose who it runs as',
  changeInChat: 'Change in chat',
  shapeAdd: 'Adds {field} to {table} · {n} entries get “{default}” · v{published} keeps working',
  shapeRename: 'Renames {old} to {new} · v{published} keeps using {old} until you publish',
  shapeDropEditor:
    'Removes {field} from {n} entries ({m} have a value). A restore point is saved first.',
  shapeDropWaiting: 'Waiting for an editor to remove {field}. {names} can confirm.',
  shapeDropInUse:
    '{field} is still used by v{published}, which {audience} uses. It’s removed when you publish v{draft}.',
  shapeDropDone: 'Removed {field} from draft v{draft} · restore point {time}',
  shapeBusy: 'Couldn’t change {table} yet: it’s busy. Trying again at {time}.',
  shapeUndo: 'Undo',
  shapeRemove: 'Remove the field',
  shapeKeep: 'Keep the field',
  shapeRestore: 'Restore',
  shapeTryNow: 'Try now',
};

export type AppOperationsCopyKey = keyof typeof en;
type Copy = Record<AppOperationsCopyKey, string>;

const sv: Copy = {
  close: 'Stäng',
  cancel: 'Avbryt',
  dupTitle: 'Duplicera {app}',
  dupName: 'Namn',
  dupNameDefault: '{app} kopia',
  dupWhat: 'Vad som följer med',
  dupCodeKey: 'Kod och inställningar',
  dupCodeValue: 'Allt i v{n}, som ett nytt privat utkast',
  dupWhoKey: 'Vem kan öppna den',
  dupWhoValue: 'Bara du, tills du publicerar den',
  dupConnKey: 'Anslutningar',
  dupConnValue: 'Kopieras inte. Den ber om att få läsa {systems} igen när den först behöver dem',
  dupHistoryKey: 'Historik',
  dupHistoryValue: 'Radhistorik, versioner och återställningspunkter stannar hos {app}',
  dupEntries: 'Poster',
  dupEmpty: 'Börja tomt',
  dupEmptyHint: 'Samma tabeller och fält, inga poster.',
  dupCopy: 'Kopiera posterna som de är nu',
  dupCopyPreview: '{tables} tabeller · {entries} poster · {files} filer · per {time}',
  dupCopyUpdated: 'Uppdaterat: {entries} poster nu',
  dupCopyLater: 'Senare ändringar i {app} når inte kopian.',
  dupCopyUnavailable: 'Det går inte att kopiera poster för den här appen: {reason}.',
  dupConfirm: 'Duplicera',
  dupBusy: 'Duplicerar…',
  dupDone: 'Skapade {name}. Bara du kan öppna den.',
  dupUndo: 'Ångra',
  dupFailed: 'Posterna kunde inte kopieras. Inget skapades.',
  dupUncertain:
    'Vi vet inte om kopian skapades. Kontrollera innan du försöker igen, så att du inte får två.',
  tryAgain: 'Försök igen',
  pausedPill: 'Pausad',
  stepOf: 'Steg {n} av {m}',
  nothingTwice: 'Inget görs två gånger. Den fortsätter från {step}.',
  approvalBody: 'Väntar på ditt OK för att {operation}.',
  approvalOthers: 'Väntar på {names}.',
  budgetBody:
    'Pausad vid budgeten: {spent} av {limit}. Den senaste fungerande versionen finns kvar som utkast.',
  providerBody: '{system} svarade inte (försökte {n} gånger).',
  viewerBody:
    '{run} hoppade över {part}: den läser var och ens eget {system}, och den här körningen har ingen person att läsa som.',
  unknownBody: 'Vi vet inte om {operation} gick igenom. {system} bekräftade inte.',
  review: 'Granska',
  stopHere: 'Stanna här',
  stopConfirm: 'Stanna här? Den senaste fungerande versionen finns kvar som utkast.',
  stopYes: 'Stanna',
  stopKeep: 'Fortsätt',
  continueMore: 'Fortsätt med {amount} till',
  checkAgain: 'Kontrollera igen',
  chooseRunAs: 'Välj vem den körs som',
  changeInChat: 'Ändra i chatten',
  shapeAdd:
    'Lägger till {field} i {table} · {n} poster får ”{default}” · v{published} fortsätter fungera',
  shapeRename: 'Byter namn på {old} till {new} · v{published} använder {old} tills du publicerar',
  shapeDropEditor:
    'Tar bort {field} från {n} poster ({m} har ett värde). En återställningspunkt sparas först.',
  shapeDropWaiting: 'Väntar på att en redaktör tar bort {field}. {names} kan bekräfta.',
  shapeDropInUse:
    '{field} används fortfarande av v{published}, som {audience} använder. Det tas bort när du publicerar v{draft}.',
  shapeDropDone: 'Tog bort {field} från utkast v{draft} · återställningspunkt {time}',
  shapeBusy: 'Kunde inte ändra {table} än: den är upptagen. Försöker igen {time}.',
  shapeUndo: 'Ångra',
  shapeRemove: 'Ta bort fältet',
  shapeKeep: 'Behåll fältet',
  shapeRestore: 'Återställ',
  shapeTryNow: 'Försök nu',
};

// Full translations (DES2); keys and placeholders identical to `en`.
const translated: Record<string, Copy> = {
  bg: {
    close: 'Затвори',
    cancel: 'Отказ',
    dupTitle: 'Дублиране на {app}',
    dupName: 'Име',
    dupNameDefault: '{app} (копие)',
    dupWhat: 'Какво се копира',
    dupCodeKey: 'Код и настройки',
    dupCodeValue: 'Всичко от v{n} като нова лична чернова',
    dupWhoKey: 'Кой може да го отваря',
    dupWhoValue: 'Само вие, докато не го публикувате',
    dupConnKey: 'Връзки',
    dupConnValue:
      'Не се копират. Ще поиска отново достъп за четене до {systems}, когато за първи път има нужда от тях',
    dupHistoryKey: 'История',
    dupHistoryValue: 'Историята на редовете, версиите и точките за възстановяване остават в {app}',
    dupEntries: 'Записи',
    dupEmpty: 'Започване без записи',
    dupEmptyHint: 'Същите таблици и полета, без записи.',
    dupCopy: 'Копиране на записите в текущия им вид',
    dupCopyPreview: '{tables} таблици · {entries} записа · {files} файла · към {time}',
    dupCopyUpdated: 'Обновено: вече {entries} записа',
    dupCopyLater: 'По-късните промени в {app} не достигат до копието.',
    dupCopyUnavailable: 'Копирането на записи не е достъпно за това приложение: {reason}.',
    dupConfirm: 'Дублиране',
    dupBusy: 'Дублиране…',
    dupDone: 'Създадено: {name}. Само вие можете да го отворите.',
    dupUndo: 'Отмяна',
    dupFailed: 'Записите не можаха да бъдат копирани. Нищо не е създадено.',
    dupUncertain:
      'Не знаем дали копието е създадено. Проверете, преди да опитате отново, за да не получите две.',
    tryAgain: 'Опитайте отново',
    pausedPill: 'На пауза',
    stepOf: 'Стъпка {n} от {m}',
    nothingTwice: 'Нищо не се прави два пъти. Продължава от: {step}.',
    approvalBody: 'Изчаква се вашето одобрение за: {operation}.',
    approvalOthers: 'Изчаква се: {names}.',
    budgetBody:
      'Пауза при достигане на бюджета: {spent} от {limit}. Последната работеща версия остава като чернова.',
    providerBody: '{system} не отговори (опити: {n}).',
    viewerBody:
      '{run} пропусна {part}: тази част чете собствения {system} на всеки, а при това изпълнение няма човек, от чието име да се чете.',
    unknownBody: 'Не знаем дали операцията ({operation}) е преминала. {system} не я потвърди.',
    review: 'Преглед',
    stopHere: 'Спиране тук',
    stopConfirm: 'Да се спре ли тук? Последната работеща версия остава като чернова.',
    stopYes: 'Спиране',
    stopKeep: 'Продължаване',
    continueMore: 'Продължаване с още {amount}',
    checkAgain: 'Нова проверка',
    chooseRunAs: 'Изберете от чие име да се изпълнява',
    changeInChat: 'Промяна в чата',
    shapeAdd:
      'Добавя полето {field} към таблицата {table} · {n} записа получават „{default}“ · v{published} продължава да работи',
    shapeRename: 'Преименува {old} на {new} · v{published} използва {old}, докато не публикувате',
    shapeDropEditor:
      'Премахва полето {field} от {n} записа ({m} имат стойност). Първо се запазва точка за възстановяване.',
    shapeDropWaiting:
      'Изчаква се редактор да премахне полето {field}. Могат да потвърдят: {names}.',
    shapeDropInUse:
      'Полето {field} все още се използва от v{published}, която се използва от {audience}. Ще бъде премахнато, когато публикувате v{draft}.',
    shapeDropDone:
      'Полето {field} е премахнато от черновата v{draft} · точка за възстановяване {time}',
    shapeBusy: 'Таблицата {table} още не може да се промени, защото е заета. Нов опит в {time}.',
    shapeUndo: 'Отмяна',
    shapeRemove: 'Премахване на полето',
    shapeKeep: 'Запазване на полето',
    shapeRestore: 'Възстановяване',
    shapeTryNow: 'Опит сега',
  },
  cs: {
    close: 'Zavřít',
    cancel: 'Zrušit',
    dupTitle: 'Duplikovat aplikaci {app}',
    dupName: 'Název',
    dupNameDefault: '{app} – kopie',
    dupWhat: 'Co se zkopíruje',
    dupCodeKey: 'Kód a nastavení',
    dupCodeValue: 'Vše z v{n} jako nový soukromý koncept',
    dupWhoKey: 'Kdo ji může otevřít',
    dupWhoValue: 'Jen vy, dokud ji nepublikujete',
    dupConnKey: 'Připojení',
    dupConnValue:
      'Nekopírují se. Až je bude aplikace poprvé potřebovat, znovu požádá o čtení: {systems}',
    dupHistoryKey: 'Historie',
    dupHistoryValue: 'Historie řádků, verze a body obnovení zůstávají v aplikaci {app}',
    dupEntries: 'Záznamy',
    dupEmpty: 'Začít s prázdnou',
    dupEmptyHint: 'Stejné tabulky a pole, žádné záznamy.',
    dupCopy: 'Zkopírovat záznamy v aktuálním stavu',
    dupCopyPreview: 'Tabulky: {tables} · záznamy: {entries} · soubory: {files} · stav k {time}',
    dupCopyUpdated: 'Aktualizováno · záznamy teď: {entries}',
    dupCopyLater: 'Pozdější změny v aplikaci {app} se do kopie nepřenesou.',
    dupCopyUnavailable: 'Kopírování záznamů není pro tuto aplikaci dostupné: {reason}.',
    dupConfirm: 'Duplikovat',
    dupBusy: 'Duplikování…',
    dupDone: 'Vytvořeno: {name}. Otevřít ji můžete jen vy.',
    dupUndo: 'Vrátit zpět',
    dupFailed: 'Záznamy se nepodařilo zkopírovat. Nic nebylo vytvořeno.',
    dupUncertain:
      'Nevíme, jestli byla kopie vytvořena. Než to zkusíte znovu, zkontrolujte to, abyste neměli dvě.',
    tryAgain: 'Zkusit znovu',
    pausedPill: 'Pozastaveno',
    stepOf: 'Krok {n} z {m}',
    nothingTwice: 'Nic se neprovede dvakrát. Pokračuje se od kroku: {step}.',
    approvalBody: 'Čeká se na váš souhlas: {operation}.',
    approvalOthers: 'Čeká se na: {names}.',
    budgetBody:
      'Pozastaveno na limitu rozpočtu: {spent} z {limit}. Poslední funkční verze zůstane jako koncept.',
    providerBody: 'Systém {system} neodpověděl (počet pokusů: {n}).',
    viewerBody:
      'Spuštění {run} přeskočilo část: {part}. Ta čte vlastní systém {system} každého člověka a toto spuštění neběží za žádnou osobu.',
    unknownBody:
      'Nevíme, jestli byla operace provedena: {operation}. Systém {system} ji nepotvrdil.',
    review: 'Prohlédnout',
    stopHere: 'Zastavit tady',
    stopConfirm: 'Zastavit tady? Poslední funkční verze zůstane jako koncept.',
    stopYes: 'Zastavit',
    stopKeep: 'Pokračovat',
    continueMore: 'Pokračovat s dalším rozpočtem: {amount}',
    checkAgain: 'Zkontrolovat znovu',
    chooseRunAs: 'Vybrat, za koho se spouští',
    changeInChat: 'Změnit v chatu',
    shapeAdd:
      'Přidá pole {field} do tabulky {table} · záznamy s hodnotou „{default}“: {n} · v{published} dál funguje',
    shapeRename: 'Přejmenuje {old} na {new} · v{published} používá {old}, dokud nepublikujete',
    shapeDropEditor:
      'Odebere pole {field} ze záznamů (počet: {n}; s hodnotou: {m}). Nejprve se uloží bod obnovení.',
    shapeDropWaiting: 'Čeká se, až editor odebere pole {field}. Potvrdit mohou: {names}.',
    shapeDropInUse:
      'Pole {field} stále používá v{published}, kterou používá {audience}. Odebere se, až publikujete v{draft}.',
    shapeDropDone: 'Pole {field} odebráno z konceptu v{draft} · bod obnovení: {time}',
    shapeBusy: 'Tabulku {table} zatím nelze změnit: je zaneprázdněná. Další pokus v {time}.',
    shapeUndo: 'Vrátit zpět',
    shapeRemove: 'Odebrat pole',
    shapeKeep: 'Ponechat pole',
    shapeRestore: 'Obnovit',
    shapeTryNow: 'Zkusit teď',
  },
  da: {
    close: 'Luk',
    cancel: 'Annuller',
    dupTitle: 'Dupliker {app}',
    dupName: 'Navn',
    dupNameDefault: 'Kopi af {app}',
    dupWhat: 'Hvad følger med',
    dupCodeKey: 'Kode og indstillinger',
    dupCodeValue: 'Alt i v{n} som en ny privat kladde',
    dupWhoKey: 'Hvem kan åbne den',
    dupWhoValue: 'Kun dig, indtil du udgiver den',
    dupConnKey: 'Forbindelser',
    dupConnValue:
      'Kopieres ikke. Den beder om at læse {systems} igen, første gang den har brug for dem',
    dupHistoryKey: 'Historik',
    dupHistoryValue: 'Rækkehistorik, versioner og gendannelsespunkter bliver i {app}',
    dupEntries: 'Poster',
    dupEmpty: 'Start tom',
    dupEmptyHint: 'Samme tabeller og felter, ingen poster.',
    dupCopy: 'Kopiér posterne, som de er nu',
    dupCopyPreview: '{tables} tabeller · {entries} poster · {files} filer · pr. {time}',
    dupCopyUpdated: 'Opdateret: {entries} poster nu',
    dupCopyLater: 'Senere ændringer i {app} når ikke kopien.',
    dupCopyUnavailable: 'Kopiering af poster er ikke tilgængelig for denne app: {reason}.',
    dupConfirm: 'Dupliker',
    dupBusy: 'Duplikerer…',
    dupDone: '{name} er oprettet. Kun du kan åbne den.',
    dupUndo: 'Fortryd',
    dupFailed: 'Posterne kunne ikke kopieres. Intet blev oprettet.',
    dupUncertain:
      'Vi ved ikke, om kopien blev oprettet. Tjek, før du prøver igen, så du ikke får to.',
    tryAgain: 'Prøv igen',
    pausedPill: 'Sat på pause',
    stepOf: 'Trin {n} af {m}',
    nothingTwice: 'Intet gøres to gange. Den fortsætter fra {step}.',
    approvalBody: 'Venter på dit OK til: {operation}.',
    approvalOthers: 'Venter på {names}.',
    budgetBody:
      'Sat på pause ved budgettet: {spent} af {limit}. Den seneste fungerende version bliver som kladde.',
    providerBody: '{system} svarede ikke (forsøgt {n} gange).',
    viewerBody:
      '{run} sprang {part} over: den læser hver persons egen {system}, og denne kørsel har ingen person at læse som.',
    unknownBody: 'Vi ved ikke, om {operation} gik igennem. {system} bekræftede det ikke.',
    review: 'Gennemse',
    stopHere: 'Stop her',
    stopConfirm: 'Stop her? Den seneste fungerende version bliver som kladde.',
    stopYes: 'Stands',
    stopKeep: 'Fortsæt',
    continueMore: 'Fortsæt med {amount} mere',
    checkAgain: 'Tjek igen',
    chooseRunAs: 'Vælg, hvem den kører som',
    changeInChat: 'Ændr i chatten',
    shapeAdd:
      'Tilføjer {field} til {table} · {n} poster får »{default}« · v{published} virker fortsat',
    shapeRename: 'Omdøber {old} til {new} · v{published} bruger fortsat {old}, indtil du udgiver',
    shapeDropEditor:
      'Fjerner {field} fra {n} poster ({m} har en værdi). Der gemmes først et gendannelsespunkt.',
    shapeDropWaiting: 'Venter på, at en redaktør fjerner {field}. {names} kan bekræfte.',
    shapeDropInUse:
      '{field} bruges stadig af v{published}, som {audience} bruger. Det fjernes, når du udgiver v{draft}.',
    shapeDropDone: 'Fjernede {field} fra kladde v{draft} · gendannelsespunkt {time}',
    shapeBusy: '{table} kunne ikke ændres endnu, da den er optaget. Prøver igen kl. {time}.',
    shapeUndo: 'Fortryd',
    shapeRemove: 'Fjern feltet',
    shapeKeep: 'Behold feltet',
    shapeRestore: 'Gendan',
    shapeTryNow: 'Prøv nu',
  },
  de: {
    close: 'Schließen',
    cancel: 'Abbrechen',
    dupTitle: '{app} duplizieren',
    dupName: 'App-Name',
    dupNameDefault: '{app} (Kopie)',
    dupWhat: 'Was übernommen wird',
    dupCodeKey: 'Code und Einstellungen',
    dupCodeValue: 'Alles aus v{n}, als neuer privater Entwurf',
    dupWhoKey: 'Wer sie öffnen kann',
    dupWhoValue: 'Nur Sie, bis Sie sie veröffentlichen',
    dupConnKey: 'Verbindungen',
    dupConnValue:
      'Nicht kopiert. Die App fragt erneut nach Lesezugriff auf {systems}, wenn sie ihn zum ersten Mal braucht',
    dupHistoryKey: 'Verlauf',
    dupHistoryValue: 'Zeilenverlauf, Versionen und Wiederherstellungspunkte bleiben bei {app}',
    dupEntries: 'Einträge',
    dupEmpty: 'Leer beginnen',
    dupEmptyHint: 'Gleiche Tabellen und Felder, keine Einträge.',
    dupCopy: 'Einträge im aktuellen Stand kopieren',
    dupCopyPreview: '{tables} Tabellen · {entries} Einträge · {files} Dateien · Stand {time}',
    dupCopyUpdated: 'Aktualisiert: jetzt {entries} Einträge',
    dupCopyLater: 'Spätere Änderungen an {app} gelangen nicht in die Kopie.',
    dupCopyUnavailable: 'Einträge kopieren ist für diese App nicht verfügbar: {reason}.',
    dupConfirm: 'Duplizieren',
    dupBusy: 'Wird dupliziert…',
    dupDone: 'Kopie erstellt: {name}. Nur Sie können sie öffnen.',
    dupUndo: 'Rückgängig',
    dupFailed: 'Die Einträge konnten nicht kopiert werden. Es wurde nichts erstellt.',
    dupUncertain:
      'Wir wissen nicht, ob die Kopie erstellt wurde. Prüfen Sie das, bevor Sie es erneut versuchen, damit Sie nicht zwei erhalten.',
    tryAgain: 'Erneut versuchen',
    pausedPill: 'Pausiert',
    stepOf: 'Schritt {n} von {m}',
    nothingTwice: 'Nichts wird doppelt ausgeführt. Es geht ab {step} weiter.',
    approvalBody: 'Wartet auf Ihre Freigabe: {operation}.',
    approvalOthers: 'Wartet auf {names}.',
    budgetBody:
      'Am Budgetlimit pausiert: {spent} von {limit}. Die letzte funktionierende Version bleibt als Entwurf erhalten.',
    providerBody: '{system} hat nicht geantwortet ({n} Versuche).',
    viewerBody:
      '{run} hat {part} übersprungen: Der Teil liest das {system}-Konto jeder einzelnen Person, und dieser Lauf hat keine Person, in deren Namen er lesen kann.',
    unknownBody:
      'Wir wissen nicht, ob dies ausgeführt wurde: {operation}. {system} hat es nicht bestätigt.',
    review: 'Überprüfen',
    stopHere: 'Hier anhalten',
    stopConfirm: 'Hier anhalten? Die letzte funktionierende Version bleibt als Entwurf erhalten.',
    stopYes: 'Anhalten',
    stopKeep: 'Weitermachen',
    continueMore: 'Mit weiteren {amount} fortfahren',
    checkAgain: 'Erneut prüfen',
    chooseRunAs: 'Ausführende Person wählen',
    changeInChat: 'Im Chat ändern',
    shapeAdd:
      'Fügt {field} zu {table} hinzu · {n} Einträge erhalten „{default}“ · v{published} funktioniert weiter',
    shapeRename: 'Benennt {old} in {new} um · v{published} nutzt {old}, bis Sie veröffentlichen',
    shapeDropEditor:
      'Entfernt {field} aus {n} Einträgen ({m} haben einen Wert). Zuerst wird ein Wiederherstellungspunkt gespeichert.',
    shapeDropWaiting:
      'Wartet darauf, dass jemand mit Bearbeitungsrechten {field} entfernt. Wer bestätigen kann: {names}.',
    shapeDropInUse:
      'v{published}, die {audience} nutzt, verwendet {field} noch. Das Feld wird entfernt, wenn Sie v{draft} veröffentlichen.',
    shapeDropDone: '{field} aus Entwurf v{draft} entfernt · Wiederherstellungspunkt {time}',
    shapeBusy:
      '{table} konnte noch nicht geändert werden, da die Tabelle belegt ist. Neuer Versuch um {time}.',
    shapeUndo: 'Rückgängig',
    shapeRemove: 'Feld entfernen',
    shapeKeep: 'Feld behalten',
    shapeRestore: 'Wiederherstellen',
    shapeTryNow: 'Jetzt versuchen',
  },
  el: {
    close: 'Κλείσιμο',
    cancel: 'Ακύρωση',
    dupTitle: 'Δημιουργία αντιγράφου: {app}',
    dupName: 'Όνομα',
    dupNameDefault: '{app} (αντίγραφο)',
    dupWhat: 'Τι περιλαμβάνεται',
    dupCodeKey: 'Κώδικας και ρυθμίσεις',
    dupCodeValue: 'Ό,τι υπάρχει στην v{n}, ως νέο ιδιωτικό πρόχειρο',
    dupWhoKey: 'Ποιος μπορεί να την ανοίξει',
    dupWhoValue: 'Μόνο εσείς, μέχρι να τη δημοσιεύσετε',
    dupConnKey: 'Συνδέσεις',
    dupConnValue:
      'Δεν αντιγράφονται. Θα ζητήσει ξανά πρόσβαση ανάγνωσης ({systems}) όταν τη χρειαστεί για πρώτη φορά',
    dupHistoryKey: 'Ιστορικό',
    dupHistoryValue:
      'Το ιστορικό γραμμών, οι εκδόσεις και τα σημεία επαναφοράς μένουν στην εφαρμογή {app}',
    dupEntries: 'Εγγραφές',
    dupEmpty: 'Έναρξη χωρίς εγγραφές',
    dupEmptyHint: 'Ίδιοι πίνακες και πεδία, χωρίς εγγραφές.',
    dupCopy: 'Αντιγραφή των εγγραφών όπως είναι τώρα',
    dupCopyPreview: '{tables} πίνακες · {entries} εγγραφές · {files} αρχεία · στις {time}',
    dupCopyUpdated: 'Ενημερώθηκε: {entries} εγγραφές τώρα',
    dupCopyLater: 'Οι μεταγενέστερες αλλαγές στην εφαρμογή {app} δεν περνούν στο αντίγραφο.',
    dupCopyUnavailable: 'Η αντιγραφή εγγραφών δεν είναι διαθέσιμη για αυτή την εφαρμογή: {reason}.',
    dupConfirm: 'Δημιουργία αντιγράφου',
    dupBusy: 'Δημιουργία αντιγράφου…',
    dupDone: 'Δημιουργήθηκε η εφαρμογή {name}. Μόνο εσείς μπορείτε να την ανοίξετε.',
    dupUndo: 'Αναίρεση',
    dupFailed: 'Δεν ήταν δυνατή η αντιγραφή των εγγραφών. Δεν δημιουργήθηκε τίποτα.',
    dupUncertain:
      'Δεν γνωρίζουμε αν δημιουργήθηκε το αντίγραφο. Ελέγξτε πριν δοκιμάσετε ξανά, για να μην καταλήξετε με δύο.',
    tryAgain: 'Δοκιμάστε ξανά',
    pausedPill: 'Σε παύση',
    stepOf: 'Βήμα {n} από {m}',
    nothingTwice: 'Τίποτα δεν γίνεται δύο φορές. Συνεχίζει από: {step}.',
    approvalBody: 'Αναμένεται η έγκρισή σας για: {operation}.',
    approvalOthers: 'Αναμονή για: {names}.',
    budgetBody:
      'Σε παύση στο όριο του προϋπολογισμού: {spent} από {limit}. Η τελευταία λειτουργική έκδοση παραμένει ως πρόχειρο.',
    providerBody: 'Το {system} δεν απάντησε ({n} προσπάθειες).',
    viewerBody:
      '{run}: παραλείφθηκε το μέρος {part}, επειδή διαβάζει το {system} του καθενός και αυτή η εκτέλεση δεν έχει άτομο για λογαριασμό του οποίου να διαβάσει.',
    unknownBody:
      'Δεν γνωρίζουμε αν ολοκληρώθηκε η ενέργεια {operation}. Το {system} δεν την επιβεβαίωσε.',
    review: 'Επισκόπηση',
    stopHere: 'Διακοπή εδώ',
    stopConfirm: 'Διακοπή εδώ; Η τελευταία λειτουργική έκδοση παραμένει ως πρόχειρο.',
    stopYes: 'Διακοπή',
    stopKeep: 'Συνέχεια',
    continueMore: 'Συνέχεια με {amount} ακόμη',
    checkAgain: 'Νέος έλεγχος',
    chooseRunAs: 'Επιλέξτε ως ποιος θα εκτελείται',
    changeInChat: 'Αλλαγή στη συνομιλία',
    shapeAdd:
      'Προσθέτει το πεδίο {field} στον πίνακα {table} · {n} εγγραφές λαμβάνουν «{default}» · η v{published} συνεχίζει να λειτουργεί',
    shapeRename:
      'Αλλάζει το όνομα {old} σε {new} · η v{published} χρησιμοποιεί το όνομα {old} μέχρι να κάνετε δημοσίευση',
    shapeDropEditor:
      'Αφαιρεί το πεδίο {field} από {n} εγγραφές ({m} έχουν τιμή). Πρώτα αποθηκεύεται ένα σημείο επαναφοράς.',
    shapeDropWaiting:
      'Αναμονή για αφαίρεση του πεδίου {field} από συντάκτη. Μπορούν να επιβεβαιώσουν: {names}.',
    shapeDropInUse:
      'Το πεδίο {field} χρησιμοποιείται ακόμη από την v{published}, την οποία χρησιμοποιεί {audience}. Αφαιρείται όταν δημοσιεύσετε την v{draft}.',
    shapeDropDone:
      'Το πεδίο {field} αφαιρέθηκε από το πρόχειρο v{draft} · σημείο επαναφοράς {time}',
    shapeBusy:
      'Ο πίνακας {table} δεν άλλαξε ακόμη, επειδή είναι απασχολημένος. Νέα προσπάθεια στις {time}.',
    shapeUndo: 'Αναίρεση',
    shapeRemove: 'Αφαίρεση πεδίου',
    shapeKeep: 'Διατήρηση πεδίου',
    shapeRestore: 'Επαναφορά',
    shapeTryNow: 'Δοκιμή τώρα',
  },
  es: {
    close: 'Cerrar',
    cancel: 'Cancelar',
    dupTitle: 'Duplicar {app}',
    dupName: 'Nombre',
    dupNameDefault: '{app} (copia)',
    dupWhat: 'Qué se incluye',
    dupCodeKey: 'Código y ajustes',
    dupCodeValue: 'Todo lo de la v{n}, como borrador privado nuevo',
    dupWhoKey: 'Quién puede abrirla',
    dupWhoValue: 'Solo tú, hasta que la publiques',
    dupConnKey: 'Conexiones',
    dupConnValue:
      'No se copian. La app vuelve a pedir permiso para leer {systems} la primera vez que lo necesite',
    dupHistoryKey: 'Historial',
    dupHistoryValue:
      'El historial de filas, las versiones y los puntos de restauración se quedan en {app}',
    dupEntries: 'Entradas',
    dupEmpty: 'Empezar vacía',
    dupEmptyHint: 'Mismas tablas y campos, sin entradas.',
    dupCopy: 'Copiar las entradas tal como están ahora',
    dupCopyPreview: '{tables} tablas · {entries} entradas · {files} archivos · a las {time}',
    dupCopyUpdated: 'Actualizado: ahora hay {entries} entradas',
    dupCopyLater: 'Los cambios posteriores en {app} no llegan a la copia.',
    dupCopyUnavailable: 'Copiar entradas no está disponible para esta app: {reason}.',
    dupConfirm: 'Duplicar',
    dupBusy: 'Duplicando…',
    dupDone: 'Copia creada: {name}. Solo tú puedes abrirla.',
    dupUndo: 'Deshacer',
    dupFailed: 'No se pudieron copiar las entradas. No se creó nada.',
    dupUncertain:
      'No sabemos si se creó la copia. Compruébalo antes de volver a intentarlo, para no tener dos.',
    tryAgain: 'Reintentar',
    pausedPill: 'En pausa',
    stepOf: 'Paso {n} de {m}',
    nothingTwice: 'Nada se hace dos veces. Continúa desde {step}.',
    approvalBody: 'Esperando tu OK para {operation}.',
    approvalOthers: 'Esperando a {names}.',
    budgetBody:
      'En pausa por el presupuesto: {spent} de {limit}. La última versión que funciona se queda como borrador.',
    providerBody: '{system} no respondió ({n} intentos).',
    viewerBody:
      '{run} omitió {part}: lee la cuenta de {system} de cada persona, y esta ejecución no tiene ninguna persona en cuyo nombre leer.',
    unknownBody: 'No sabemos si esta operación se completó: {operation}. {system} no la confirmó.',
    review: 'Revisar',
    stopHere: 'Detener aquí',
    stopConfirm: '¿Detener aquí? La última versión que funciona se queda como borrador.',
    stopYes: 'Detener',
    stopKeep: 'Continuar',
    continueMore: 'Continuar con {amount} más',
    checkAgain: 'Volver a comprobar',
    chooseRunAs: 'Elegir en nombre de quién se ejecuta',
    changeInChat: 'Cambiar en el chat',
    shapeAdd:
      'Añade {field} a {table} · {n} entradas reciben «{default}» · la v{published} sigue funcionando',
    shapeRename:
      'Cambia el nombre de {old} a {new} · la v{published} sigue usando {old} hasta que publiques',
    shapeDropEditor:
      'Quita {field} de {n} entradas ({m} tienen un valor). Primero se guarda un punto de restauración.',
    shapeDropWaiting:
      'Esperando a que alguien con permiso de edición quite {field}. Quién puede confirmar: {names}.',
    shapeDropInUse:
      'La v{published}, que usa {audience}, todavía usa {field}. El campo se quitará cuando publiques la v{draft}.',
    shapeDropDone: 'Se quitó {field} del borrador v{draft} · punto de restauración {time}',
    shapeBusy:
      'Aún no se pudo cambiar {table}: la tabla está ocupada. Se volverá a intentar a las {time}.',
    shapeUndo: 'Deshacer',
    shapeRemove: 'Quitar el campo',
    shapeKeep: 'Conservar el campo',
    shapeRestore: 'Restaurar',
    shapeTryNow: 'Intentar ahora',
  },
  et: {
    close: 'Sulge',
    cancel: 'Tühista',
    dupTitle: 'Tee koopia: {app}',
    dupName: 'Nimi',
    dupNameDefault: '{app} (koopia)',
    dupWhat: 'Mis kaasa tuleb',
    dupCodeKey: 'Kood ja seaded',
    dupCodeValue: 'Kõik versioonist v{n} uue privaatse mustandina',
    dupWhoKey: 'Kes saab seda avada',
    dupWhoValue: 'Ainult sina, kuni selle avaldad',
    dupConnKey: 'Ühendused',
    dupConnValue:
      'Ei kopeerita. See küsib uuesti lugemisluba ({systems}), kui seda esimest korda vaja läheb',
    dupHistoryKey: 'Ajalugu',
    dupHistoryValue: 'Ridade ajalugu, versioonid ja taastepunktid jäävad rakendusse {app}',
    dupEntries: 'Kirjed',
    dupEmpty: 'Alusta tühjalt',
    dupEmptyHint: 'Samad tabelid ja väljad, kirjeid pole.',
    dupCopy: 'Kopeeri kirjed praegusel kujul',
    dupCopyPreview: '{tables} tabelit · {entries} kirjet · {files} faili · seisuga {time}',
    dupCopyUpdated: 'Uuendatud: nüüd {entries} kirjet',
    dupCopyLater: 'Hilisemad muudatused rakenduses {app} koopiasse ei jõua.',
    dupCopyUnavailable: 'Kirjete kopeerimine pole selle rakenduse puhul saadaval: {reason}.',
    dupConfirm: 'Tee koopia',
    dupBusy: 'Koopia tegemine…',
    dupDone: 'Loodud: {name}. Ainult sina saad seda avada.',
    dupUndo: 'Võta tagasi',
    dupFailed: 'Kirjeid ei saanud kopeerida. Midagi ei loodud.',
    dupUncertain:
      'Me ei tea, kas koopia loodi. Kontrolli enne uuesti proovimist, et sul ei tekiks kahte.',
    tryAgain: 'Proovi uuesti',
    pausedPill: 'Peatatud',
    stepOf: 'Samm {n} / {m}',
    nothingTwice: 'Midagi ei tehta kaks korda. Jätkatakse sammust {step}.',
    approvalBody: 'Ootab sinu nõusolekut: {operation}.',
    approvalOthers: 'Ootab: {names}.',
    budgetBody:
      'Peatatud eelarve piiril: {spent} / {limit}. Viimane töötav versioon jääb mustandiks.',
    providerBody: '{system} ei vastanud (proovitud {n} korda).',
    viewerBody:
      '{run} jättis osa {part} vahele: see loeb igaühe enda {system}-kontot, aga sellel käivitusel pole inimest, kelle nimel lugeda.',
    unknownBody: 'Me ei tea, kas {operation} läks läbi. {system} ei kinnitanud seda.',
    review: 'Vaata üle',
    stopHere: 'Peata siin',
    stopConfirm: 'Kas peatada siin? Viimane töötav versioon jääb mustandiks.',
    stopYes: 'Peata',
    stopKeep: 'Jätka',
    continueMore: 'Jätka veel {amount} võrra',
    checkAgain: 'Kontrolli uuesti',
    chooseRunAs: 'Vali, kelle nimel see töötab',
    changeInChat: 'Muuda vestluses',
    shapeAdd:
      'Lisab välja {field} tabelisse {table} · {n} kirjet saavad väärtuse „{default}“ · v{published} töötab edasi',
    shapeRename: 'Nimetab {old} ümber: {new} · v{published} kasutab nime {old}, kuni avaldad',
    shapeDropEditor:
      'Eemaldab välja {field} {n} kirjelt ({m} kirjel on väärtus). Kõigepealt salvestatakse taastepunkt.',
    shapeDropWaiting: 'Ootab, et toimetaja eemaldaks välja {field}. Kinnitada saavad: {names}.',
    shapeDropInUse:
      'Välja {field} kasutab endiselt v{published}, mida kasutab {audience}. See eemaldatakse, kui avaldad v{draft}.',
    shapeDropDone: 'Väli {field} eemaldati mustandist v{draft} · taastepunkt {time}',
    shapeBusy:
      'Tabelit {table} ei saanud veel muuta, sest see on hõivatud. Proovime uuesti kell {time}.',
    shapeUndo: 'Võta tagasi',
    shapeRemove: 'Eemalda väli',
    shapeKeep: 'Jäta väli alles',
    shapeRestore: 'Taasta',
    shapeTryNow: 'Proovi kohe',
  },
  fi: {
    close: 'Sulje',
    cancel: 'Peruuta',
    dupTitle: 'Monista {app}',
    dupName: 'Nimi',
    dupNameDefault: '{app} (kopio)',
    dupWhat: 'Mitä mukana tulee',
    dupCodeKey: 'Koodi ja asetukset',
    dupCodeValue: 'Kaikki versiosta v{n} uutena yksityisenä luonnoksena',
    dupWhoKey: 'Kuka voi avata sen',
    dupWhoValue: 'Vain sinä, kunnes julkaiset sen',
    dupConnKey: 'Yhteydet',
    dupConnValue:
      'Ei kopioida. Se pyytää uudelleen lukuoikeutta ({systems}), kun se tarvitsee sitä ensimmäisen kerran',
    dupHistoryKey: 'Historia',
    dupHistoryValue: 'Rivihistoria, versiot ja palautuspisteet jäävät sovellukseen {app}',
    dupEntries: 'Merkinnät',
    dupEmpty: 'Aloita tyhjänä',
    dupEmptyHint: 'Samat taulukot ja kentät, ei merkintöjä.',
    dupCopy: 'Kopioi merkinnät nykyisessä muodossaan',
    dupCopyPreview: '{tables} taulukkoa · {entries} merkintää · {files} tiedostoa · tilanne {time}',
    dupCopyUpdated: 'Päivitetty: nyt {entries} merkintää',
    dupCopyLater: 'Myöhemmät muutokset sovellukseen {app} eivät siirry kopioon.',
    dupCopyUnavailable: 'Merkintöjen kopiointi ei ole käytettävissä tässä sovelluksessa: {reason}.',
    dupConfirm: 'Monista',
    dupBusy: 'Monistetaan…',
    dupDone: 'Luotiin {name}. Vain sinä voit avata sen.',
    dupUndo: 'Kumoa',
    dupFailed: 'Merkintöjä ei voitu kopioida. Mitään ei luotu.',
    dupUncertain:
      'Emme tiedä, luotiinko kopio. Tarkista ennen kuin yrität uudelleen, ettei niitä tule kahta.',
    tryAgain: 'Yritä uudelleen',
    pausedPill: 'Keskeytetty',
    stepOf: 'Vaihe {n}/{m}',
    nothingTwice: 'Mitään ei tehdä kahdesti. Se jatkaa kohdasta {step}.',
    approvalBody: 'Odottaa hyväksyntääsi: {operation}.',
    approvalOthers: 'Odottaa: {names}.',
    budgetBody:
      'Keskeytetty budjetin rajalla: {spent}/{limit}. Viimeisin toimiva versio jää luonnokseksi.',
    providerBody: '{system} ei vastannut (yrityksiä {n}).',
    viewerBody:
      '{run} ohitti osan {part}: se lukee kunkin omaa {system}-tiliä, eikä tällä suorituksella ole henkilöä, jona lukea.',
    unknownBody: 'Emme tiedä, menikö {operation} läpi. {system} ei vahvistanut sitä.',
    review: 'Tarkastele',
    stopHere: 'Lopeta tähän',
    stopConfirm: 'Lopetetaanko tähän? Viimeisin toimiva versio jää luonnokseksi.',
    stopYes: 'Lopeta',
    stopKeep: 'Jatka',
    continueMore: 'Jatka vielä {amount}',
    checkAgain: 'Tarkista uudelleen',
    chooseRunAs: 'Valitse, kenenä se suoritetaan',
    changeInChat: 'Muuta chatissa',
    shapeAdd:
      'Lisää kentän {field} taulukkoon {table} · {n} merkintää saa arvon ”{default}” · v{published} toimii edelleen',
    shapeRename:
      'Nimeää kohteen {old} uudelleen: {new} · v{published} käyttää nimeä {old}, kunnes julkaiset',
    shapeDropEditor:
      'Poistaa kentän {field} {n} merkinnästä ({m} sisältää arvon). Ensin tallennetaan palautuspiste.',
    shapeDropWaiting: 'Odotetaan, että muokkaaja poistaa kentän {field}. Vahvistaa voi: {names}.',
    shapeDropInUse:
      'Kenttää {field} käyttää yhä v{published}, jota {audience} käyttää. Se poistetaan, kun julkaiset version v{draft}.',
    shapeDropDone: 'Kenttä {field} poistettu luonnoksesta v{draft} · palautuspiste {time}',
    shapeBusy:
      'Taulukkoa {table} ei voitu vielä muuttaa, koska se on varattu. Yritetään uudelleen klo {time}.',
    shapeUndo: 'Kumoa',
    shapeRemove: 'Poista kenttä',
    shapeKeep: 'Säilytä kenttä',
    shapeRestore: 'Palauta',
    shapeTryNow: 'Yritä nyt',
  },
  fr: {
    close: 'Fermer',
    cancel: 'Annuler',
    dupTitle: 'Dupliquer {app}',
    dupName: 'Nom',
    dupNameDefault: '{app} (copie)',
    dupWhat: 'Ce qui est inclus',
    dupCodeKey: 'Code et paramètres',
    dupCodeValue: 'Tout le contenu de la v{n}, comme nouveau brouillon privé',
    dupWhoKey: 'Qui peut l’ouvrir',
    dupWhoValue: 'Uniquement vous, jusqu’à sa publication',
    dupConnKey: 'Connexions',
    dupConnValue:
      'Non copiées. L’app redemande l’accès en lecture à {systems} la première fois qu’elle en a besoin',
    dupHistoryKey: 'Historique',
    dupHistoryValue:
      'L’historique des lignes, les versions et les points de restauration restent dans {app}',
    dupEntries: 'Entrées',
    dupEmpty: 'Commencer à vide',
    dupEmptyHint: 'Mêmes tableaux et champs, aucune entrée.',
    dupCopy: 'Copier les entrées dans leur état actuel',
    dupCopyPreview: '{tables} tableaux · {entries} entrées · {files} fichiers · à {time}',
    dupCopyUpdated: 'Mis à jour : {entries} entrées désormais',
    dupCopyLater: 'Les modifications ultérieures de {app} ne sont pas reportées dans la copie.',
    dupCopyUnavailable: 'La copie des entrées n’est pas disponible pour cette app : {reason}.',
    dupConfirm: 'Dupliquer',
    dupBusy: 'Duplication…',
    dupDone: 'Copie créée : {name}. Vous êtes la seule personne à pouvoir l’ouvrir.',
    dupUndo: 'Annuler',
    dupFailed: 'Impossible de copier les entrées. Rien n’a été créé.',
    dupUncertain:
      'Nous ne savons pas si la copie a été créée. Vérifiez avant de réessayer, pour ne pas en avoir deux.',
    tryAgain: 'Réessayer',
    pausedPill: 'En pause',
    stepOf: 'Étape {n} sur {m}',
    nothingTwice: 'Rien n’est fait deux fois. La suite reprend à partir de {step}.',
    approvalBody: 'En attente de votre accord pour {operation}.',
    approvalOthers: 'En attente de {names}.',
    budgetBody:
      'En pause, budget atteint : {spent} sur {limit}. La dernière version fonctionnelle reste en brouillon.',
    providerBody: '{system} n’a pas répondu ({n} tentatives).',
    viewerBody:
      '{run} a ignoré {part} : cette partie lit le compte {system} de chaque personne, et cette exécution n’a personne au nom de qui lire.',
    unknownBody:
      'Nous ne savons pas si cette opération a abouti : {operation}. {system} ne l’a pas confirmée.',
    review: 'Examiner',
    stopHere: 'Arrêter ici',
    stopConfirm: 'Arrêter ici ? La dernière version fonctionnelle reste en brouillon.',
    stopYes: 'Arrêter',
    stopKeep: 'Continuer',
    continueMore: 'Continuer avec {amount} de plus',
    checkAgain: 'Vérifier à nouveau',
    chooseRunAs: 'Choisir au nom de qui elle s’exécute',
    changeInChat: 'Modifier dans le chat',
    shapeAdd:
      'Ajoute {field} à {table} · {n} entrées reçoivent « {default} » · la v{published} continue de fonctionner',
    shapeRename:
      'Renomme {old} en {new} · la v{published} utilise {old} jusqu’à ce que vous publiiez',
    shapeDropEditor:
      'Supprime {field} de {n} entrées ({m} ont une valeur). Un point de restauration est d’abord enregistré.',
    shapeDropWaiting:
      'En attente qu’une personne autorisée à modifier supprime {field}. Qui peut confirmer : {names}.',
    shapeDropInUse:
      'La v{published}, utilisée par {audience}, utilise encore {field}. Le champ sera supprimé quand vous publierez la v{draft}.',
    shapeDropDone: 'Champ {field} supprimé du brouillon v{draft} · point de restauration {time}',
    shapeBusy:
      'Impossible de modifier {table} pour l’instant : le tableau est occupé. Nouvel essai à {time}.',
    shapeUndo: 'Annuler',
    shapeRemove: 'Supprimer le champ',
    shapeKeep: 'Garder le champ',
    shapeRestore: 'Restaurer',
    shapeTryNow: 'Essayer maintenant',
  },
  ga: {
    close: 'Dún',
    cancel: 'Cuir ar ceal',
    dupTitle: 'Dúblaigh {app}',
    dupName: 'Ainm',
    dupNameDefault: 'Cóip de {app}',
    dupWhat: 'Cad a thagann léi',
    dupCodeKey: 'Cód agus socruithe',
    dupCodeValue: 'Gach rud in v{n}, mar dhréacht príobháideach nua',
    dupWhoKey: 'Cé atá in ann í a oscailt',
    dupWhoValue: 'Tusa amháin, go dtí go bhfoilsíonn tú í',
    dupConnKey: 'Ceangail',
    dupConnValue:
      'Ní chóipeáiltear iad. Iarrfaidh sí cead arís {systems} a léamh nuair a bheidh siad ag teastáil den chéad uair',
    dupHistoryKey: 'Stair',
    dupHistoryValue: 'Fanann stair na rónna, na leaganacha agus na pointí athchóirithe le {app}',
    dupEntries: 'Iontrálacha',
    dupEmpty: 'Tosaigh folamh',
    dupEmptyHint: 'Na táblaí agus na réimsí céanna, gan iontrálacha.',
    dupCopy: 'Cóipeáil na hiontrálacha mar atá siad anois',
    dupCopyPreview: 'Táblaí: {tables} · iontrálacha: {entries} · comhaid: {files} · amhail {time}',
    dupCopyUpdated: 'Nuashonraithe · iontrálacha anois: {entries}',
    dupCopyLater: 'Ní shroicheann athruithe níos déanaí ar {app} an chóip.',
    dupCopyUnavailable: 'Níl cóipeáil iontrálacha ar fáil don aip seo: {reason}.',
    dupConfirm: 'Dúblaigh',
    dupBusy: 'Á dhúbailt…',
    dupDone: 'Cruthaíodh {name}. Níl ach tusa in ann í a oscailt.',
    dupUndo: 'Cealaigh',
    dupFailed: 'Níorbh fhéidir na hiontrálacha a chóipeáil. Níor cruthaíodh aon rud.',
    dupUncertain:
      'Níl a fhios againn ar cruthaíodh an chóip. Seiceáil sula mbaineann tú triail eile as, ionas nach mbeidh dhá cheann agat.',
    tryAgain: 'Bain triail eile as',
    pausedPill: 'Curtha ar sos',
    stepOf: 'Céim {n} as {m}',
    nothingTwice: 'Ní dhéantar aon rud faoi dhó. Leanann sé ar aghaidh ó: {step}.',
    approvalBody: 'Ag fanacht ar do cheadú: {operation}.',
    approvalOthers: 'Ag fanacht ar: {names}.',
    budgetBody:
      'Curtha ar sos ag an mbuiséad: {spent} as {limit}. Fanann an leagan oibre deireanach mar dhréacht.',
    providerBody: 'Níor fhreagair {system} (iarrachtaí: {n}).',
    viewerBody:
      'Scipeáil {run} “{part}”: léann an chuid sin {system} gach duine féin, agus níl aon duine ag an rith seo le léamh thar a cheann.',
    unknownBody: 'Níl a fhios againn ar éirigh leis seo: {operation}. Níor dhearbhaigh {system} é.',
    review: 'Athbhreithnigh',
    stopHere: 'Stad anseo',
    stopConfirm: 'Stad anseo? Fanann an leagan oibre deireanach mar dhréacht.',
    stopYes: 'Stad',
    stopKeep: 'Lean ar aghaidh',
    continueMore: 'Lean ar aghaidh le {amount} eile',
    checkAgain: 'Seiceáil arís',
    chooseRunAs: 'Roghnaigh cé thar a cheann a ritheann sé',
    changeInChat: 'Athraigh sa chomhrá',
    shapeAdd:
      'Cuireann sé {field} le {table} · iontrálacha a gheobhaidh “{default}”: {n} · leanann v{published} ag obair',
    shapeRename:
      'Athainmníonn sé {old} mar {new} · úsáideann v{published} {old} go dtí go bhfoilsíonn tú',
    shapeDropEditor:
      'Baineann sé {field} ó iontrálacha (iomlán: {n}; le luach: {m}). Sábháiltear pointe athchóirithe ar dtús.',
    shapeDropWaiting:
      'Ag fanacht go mbainfidh eagarthóir {field}. Is féidir le {names} é a dhearbhú.',
    shapeDropInUse:
      'Tá {field} fós in úsáid ag v{published}, a úsáideann {audience}. Bainfear é nuair a fhoilseoidh tú v{draft}.',
    shapeDropDone: 'Baineadh {field} ó dhréacht v{draft} · pointe athchóirithe {time}',
    shapeBusy:
      'Níorbh fhéidir {table} a athrú fós: tá sé gnóthach. Bainfimid triail eile as ag {time}.',
    shapeUndo: 'Cealaigh',
    shapeRemove: 'Bain an réimse',
    shapeKeep: 'Coinnigh an réimse',
    shapeRestore: 'Athchóirigh',
    shapeTryNow: 'Bain triail as anois',
  },
  hr: {
    close: 'Zatvori',
    cancel: 'Odustani',
    dupTitle: 'Dupliciraj aplikaciju {app}',
    dupName: 'Naziv',
    dupNameDefault: '{app} – kopija',
    dupWhat: 'Što se prenosi',
    dupCodeKey: 'Kôd i postavke',
    dupCodeValue: 'Sve iz v{n} kao novi privatni nacrt',
    dupWhoKey: 'Tko je može otvoriti',
    dupWhoValue: 'Samo vi, dok je ne objavite',
    dupConnKey: 'Veze',
    dupConnValue:
      'Ne kopiraju se. Kad ih aplikacija prvi put zatreba, ponovno će zatražiti čitanje: {systems}',
    dupHistoryKey: 'Povijest',
    dupHistoryValue: 'Povijest redaka, verzije i točke vraćanja ostaju u aplikaciji {app}',
    dupEntries: 'Unosi',
    dupEmpty: 'Počni prazno',
    dupEmptyHint: 'Iste tablice i polja, bez unosa.',
    dupCopy: 'Kopiraj unose u trenutačnom stanju',
    dupCopyPreview: 'Tablice: {tables} · unosi: {entries} · datoteke: {files} · stanje u {time}',
    dupCopyUpdated: 'Ažurirano · unosi sada: {entries}',
    dupCopyLater: 'Kasnije promjene u aplikaciji {app} ne prenose se u kopiju.',
    dupCopyUnavailable: 'Kopiranje unosa nije dostupno za ovu aplikaciju: {reason}.',
    dupConfirm: 'Dupliciraj',
    dupBusy: 'Dupliciranje…',
    dupDone: 'Izrađeno: {name}. Samo je vi možete otvoriti.',
    dupUndo: 'Poništi',
    dupFailed: 'Unose nije bilo moguće kopirati. Ništa nije izrađeno.',
    dupUncertain:
      'Ne znamo je li kopija izrađena. Provjerite prije ponovnog pokušaja kako ne biste dobili dvije.',
    tryAgain: 'Pokušaj ponovno',
    pausedPill: 'Pauzirano',
    stepOf: 'Korak {n} od {m}',
    nothingTwice: 'Ništa se ne izvodi dvaput. Nastavlja se od koraka: {step}.',
    approvalBody: 'Čeka se vaše odobrenje: {operation}.',
    approvalOthers: 'Čeka se: {names}.',
    budgetBody:
      'Pauzirano zbog proračuna: {spent} od {limit}. Posljednja ispravna verzija ostaje kao nacrt.',
    providerBody: 'Sustav {system} nije odgovorio (broj pokušaja: {n}).',
    viewerBody:
      'Pokretanje {run} preskočilo je dio: {part}. Taj dio čita vlastiti sustav {system} svake osobe, a ovo se pokretanje ne izvodi u ime nijedne osobe.',
    unknownBody:
      'Ne znamo je li operacija izvršena: {operation}. Sustav {system} nije je potvrdio.',
    review: 'Pregledaj',
    stopHere: 'Zaustavi ovdje',
    stopConfirm: 'Zaustaviti ovdje? Posljednja ispravna verzija ostaje kao nacrt.',
    stopYes: 'Zaustavi',
    stopKeep: 'Nastavi',
    continueMore: 'Nastavi s dodatnim proračunom: {amount}',
    checkAgain: 'Provjeri ponovno',
    chooseRunAs: 'Odaberi u čije se ime pokreće',
    changeInChat: 'Promijeni u chatu',
    shapeAdd:
      'Dodaje polje {field} u tablicu {table} · unosi s vrijednošću „{default}”: {n} · v{published} i dalje radi',
    shapeRename: 'Preimenuje {old} u {new} · v{published} koristi {old} dok ne objavite',
    shapeDropEditor:
      'Uklanja polje {field} iz unosa (broj: {n}; s vrijednošću: {m}). Najprije se sprema točka vraćanja.',
    shapeDropWaiting: 'Čeka se da urednik ukloni polje {field}. Potvrditi mogu: {names}.',
    shapeDropInUse:
      'Polje {field} još koristi v{published}, koju koristi {audience}. Uklonit će se kad objavite v{draft}.',
    shapeDropDone: 'Polje {field} uklonjeno iz nacrta v{draft} · točka vraćanja: {time}',
    shapeBusy: 'Tablicu {table} još nije moguće promijeniti: zauzeta je. Ponovni pokušaj u {time}.',
    shapeUndo: 'Poništi',
    shapeRemove: 'Ukloni polje',
    shapeKeep: 'Zadrži polje',
    shapeRestore: 'Vrati',
    shapeTryNow: 'Pokušaj sada',
  },
  hu: {
    close: 'Bezárás',
    cancel: 'Mégse',
    dupTitle: 'Másolat készítése: {app}',
    dupName: 'Név',
    dupNameDefault: '{app} (másolat)',
    dupWhat: 'Mi kerül át',
    dupCodeKey: 'Kód és beállítások',
    dupCodeValue: 'Minden a v{n} verzióból, új privát piszkozatként',
    dupWhoKey: 'Ki nyithatja meg',
    dupWhoValue: 'Csak Ön, amíg közzé nem teszi',
    dupConnKey: 'Kapcsolatok',
    dupConnValue: 'Nem másolódnak. Első használatkor újra engedélyt kér az olvasásukhoz: {systems}',
    dupHistoryKey: 'Előzmények',
    dupHistoryValue:
      'A sorelőzmények, a verziók és a visszaállítási pontok az eredetinél maradnak: {app}',
    dupEntries: 'Bejegyzések',
    dupEmpty: 'Üres kezdés',
    dupEmptyHint: 'Ugyanazok a táblák és mezők, bejegyzések nélkül.',
    dupCopy: 'A bejegyzések másolása a jelenlegi állapotukban',
    dupCopyPreview: '{tables} tábla · {entries} bejegyzés · {files} fájl · állapot: {time}',
    dupCopyUpdated: 'Frissítve: most {entries} bejegyzés',
    dupCopyLater: 'Az eredeti alkalmazás ({app}) későbbi változásai nem kerülnek át a másolatba.',
    dupCopyUnavailable: 'Ennél az alkalmazásnál a bejegyzések másolása nem érhető el: {reason}.',
    dupConfirm: 'Másolat készítése',
    dupBusy: 'Másolat készítése…',
    dupDone: 'Elkészült: {name}. Csak Ön nyithatja meg.',
    dupUndo: 'Visszavonás',
    dupFailed: 'A bejegyzéseket nem sikerült másolni. Semmi sem jött létre.',
    dupUncertain:
      'Nem tudjuk, hogy létrejött-e a másolat. Ellenőrizze, mielőtt újra próbálkozik, nehogy kettő legyen.',
    tryAgain: 'Próbálja újra',
    pausedPill: 'Szüneteltetve',
    stepOf: '{n}/{m}. lépés',
    nothingTwice: 'Semmi sem történik kétszer. Innen folytatja: {step}.',
    approvalBody: 'Az Ön jóváhagyására vár: {operation}.',
    approvalOthers: 'Rájuk vár: {names}.',
    budgetBody:
      'Szüneteltetve a keretnél: {spent} / {limit}. Az utolsó működő verzió piszkozatként megmarad.',
    providerBody: 'Nem érkezett válasz innen: {system} ({n} próbálkozás).',
    viewerBody:
      '{run} kihagyta ezt a részt: {part}. A rész mindenki saját {system}-fiókját olvassa, ennek a futtatásnak pedig nincs olyan személye, akinek a nevében olvashatna.',
    unknownBody:
      'Nem tudjuk, hogy sikerült-e a művelet: {operation}. A(z) {system} nem erősítette meg.',
    review: 'Áttekintés',
    stopHere: 'Leállítás itt',
    stopConfirm: 'Leállítja itt? Az utolsó működő verzió piszkozatként megmarad.',
    stopYes: 'Leállítás',
    stopKeep: 'Folytatás',
    continueMore: 'Folytatás további {amount} összeggel',
    checkAgain: 'Ellenőrzés újra',
    chooseRunAs: 'Válassza ki, ki nevében fusson',
    changeInChat: 'Módosítás a csevegésben',
    shapeAdd:
      'Új mező: {field} a(z) {table} táblában · {n} bejegyzés értéke „{default}” lesz · a v{published} továbbra is működik',
    shapeRename:
      'Átnevezés: {old} helyett {new} · a v{published} a közzétételig továbbra is ezt használja: {old}',
    shapeDropEditor:
      'Mező eltávolítása {n} bejegyzésből: {field} ({m} bejegyzésben van érték). Előbb mentünk egy visszaállítási pontot.',
    shapeDropWaiting:
      'Arra vár, hogy egy szerkesztő eltávolítsa ezt a mezőt: {field}. Megerősítheti: {names}.',
    shapeDropInUse:
      '{field}: ezt a mezőt még használja a v{published}, amelyet {audience} használ. A v{draft} közzétételekor eltávolítjuk.',
    shapeDropDone:
      'Mező eltávolítva a v{draft} piszkozatból: {field} · visszaállítási pont: {time}',
    shapeBusy: 'Ez a tábla még nem módosítható, mert foglalt: {table}. Újrapróbálkozás: {time}.',
    shapeUndo: 'Visszavonás',
    shapeRemove: 'Mező eltávolítása',
    shapeKeep: 'Mező megtartása',
    shapeRestore: 'Visszaállítás',
    shapeTryNow: 'Próbálkozás most',
  },
  it: {
    close: 'Chiudi',
    cancel: 'Annulla',
    dupTitle: 'Duplica {app}',
    dupName: 'Nome',
    dupNameDefault: '{app} (copia)',
    dupWhat: 'Cosa viene incluso',
    dupCodeKey: 'Codice e impostazioni',
    dupCodeValue: 'Tutto della v{n}, come nuova bozza privata',
    dupWhoKey: 'Chi può aprirla',
    dupWhoValue: 'Solo tu, finché non la pubblichi',
    dupConnKey: 'Connessioni',
    dupConnValue:
      'Non copiate. L’app chiede di nuovo di leggere {systems} la prima volta che ne ha bisogno',
    dupHistoryKey: 'Cronologia',
    dupHistoryValue:
      'La cronologia delle righe, le versioni e i punti di ripristino restano in {app}',
    dupEntries: 'Voci',
    dupEmpty: 'Inizia vuota',
    dupEmptyHint: 'Stesse tabelle e campi, nessuna voce.',
    dupCopy: 'Copia le voci così come sono ora',
    dupCopyPreview: '{tables} tabelle · {entries} voci · {files} file · alle {time}',
    dupCopyUpdated: 'Aggiornato: ora {entries} voci',
    dupCopyLater: 'Le modifiche successive a {app} non arrivano alla copia.',
    dupCopyUnavailable: 'La copia delle voci non è disponibile per questa app: {reason}.',
    dupConfirm: 'Duplica',
    dupBusy: 'Duplicazione in corso…',
    dupDone: 'Copia creata: {name}. Solo tu puoi aprirla.',
    dupUndo: 'Annulla',
    dupFailed: 'Non è stato possibile copiare le voci. Non è stato creato nulla.',
    dupUncertain:
      'Non sappiamo se la copia è stata creata. Controlla prima di riprovare, così non ne avrai due.',
    tryAgain: 'Riprova',
    pausedPill: 'In pausa',
    stepOf: 'Passaggio {n} di {m}',
    nothingTwice: 'Niente viene fatto due volte. Riprende da {step}.',
    approvalBody: 'In attesa del tuo OK per {operation}.',
    approvalOthers: 'In attesa di {names}.',
    budgetBody:
      'In pausa per il budget: {spent} su {limit}. L’ultima versione funzionante resta come bozza.',
    providerBody: '{system} non ha risposto ({n} tentativi).',
    viewerBody:
      '{run} ha saltato {part}: legge l’account {system} di ciascuno e questa esecuzione non ha nessuna persona a nome di cui leggere.',
    unknownBody:
      'Non sappiamo se questa operazione è andata a buon fine: {operation}. {system} non l’ha confermata.',
    review: 'Rivedi',
    stopHere: 'Fermati qui',
    stopConfirm: 'Fermarsi qui? L’ultima versione funzionante resta come bozza.',
    stopYes: 'Ferma',
    stopKeep: 'Continua',
    continueMore: 'Continua con altri {amount}',
    checkAgain: 'Controlla di nuovo',
    chooseRunAs: 'Scegli a nome di chi viene eseguita',
    changeInChat: 'Modifica in chat',
    shapeAdd:
      'Aggiunge {field} a {table} · {n} voci ricevono «{default}» · la v{published} continua a funzionare',
    shapeRename:
      'Rinomina {old} in {new} · la v{published} continua a usare {old} finché non pubblichi',
    shapeDropEditor:
      'Rimuove {field} da {n} voci ({m} hanno un valore). Prima viene salvato un punto di ripristino.',
    shapeDropWaiting:
      'In attesa che qualcuno con permesso di modifica rimuova {field}. Chi può confermare: {names}.',
    shapeDropInUse:
      'La v{published}, usata da {audience}, usa ancora {field}. Il campo verrà rimosso quando pubblichi la v{draft}.',
    shapeDropDone: 'Campo {field} rimosso dalla bozza v{draft} · punto di ripristino {time}',
    shapeBusy:
      'Non è stato ancora possibile modificare {table}: la tabella è occupata. Nuovo tentativo alle {time}.',
    shapeUndo: 'Annulla',
    shapeRemove: 'Rimuovi il campo',
    shapeKeep: 'Mantieni il campo',
    shapeRestore: 'Ripristina',
    shapeTryNow: 'Prova ora',
  },
  lt: {
    close: 'Uždaryti',
    cancel: 'Atšaukti',
    dupTitle: 'Dubliuoti {app}',
    dupName: 'Pavadinimas',
    dupNameDefault: '{app} kopija',
    dupWhat: 'Kas perkeliama kartu',
    dupCodeKey: 'Kodas ir nustatymai',
    dupCodeValue: 'Viskas iš v{n} kaip naujas privatus juodraštis',
    dupWhoKey: 'Kas gali atidaryti',
    dupWhoValue: 'Tik jūs, kol jos nepaskelbsite',
    dupConnKey: 'Jungtys',
    dupConnValue:
      'Nekopijuojamos. Kai pirmą kartą prireiks, ji vėl paprašys leidimo skaityti: {systems}',
    dupHistoryKey: 'Istorija',
    dupHistoryValue: 'Eilučių istorija, versijos ir atkūrimo taškai lieka programėlėje {app}',
    dupEntries: 'Įrašai',
    dupEmpty: 'Pradėti tuščią',
    dupEmptyHint: 'Tos pačios lentelės ir laukai, be įrašų.',
    dupCopy: 'Kopijuoti įrašus tokius, kokie yra dabar',
    dupCopyPreview: 'Lentelės: {tables} · įrašai: {entries} · failai: {files} · būsena {time}',
    dupCopyUpdated: 'Atnaujinta · įrašai dabar: {entries}',
    dupCopyLater: 'Vėlesni programėlės {app} pakeitimai kopijos nepasieks.',
    dupCopyUnavailable: 'Šiai programėlei įrašų kopijuoti negalima: {reason}.',
    dupConfirm: 'Dubliuoti',
    dupBusy: 'Dubliuojama…',
    dupDone: 'Sukurta: {name}. Atidaryti galite tik jūs.',
    dupUndo: 'Anuliuoti',
    dupFailed: 'Nepavyko nukopijuoti įrašų. Niekas nesukurta.',
    dupUncertain:
      'Nežinome, ar kopija buvo sukurta. Prieš bandydami dar kartą patikrinkite, kad neturėtumėte dviejų.',
    tryAgain: 'Bandyti dar kartą',
    pausedPill: 'Pristabdyta',
    stepOf: 'Žingsnis {n} iš {m}',
    nothingTwice: 'Niekas nekartojama. Tęsiama nuo žingsnio „{step}“.',
    approvalBody: 'Laukiama jūsų patvirtinimo: {operation}.',
    approvalOthers: 'Laukiama: {names}.',
    budgetBody:
      'Pristabdyta pasiekus biudžetą: {spent} iš {limit}. Paskutinė veikianti versija lieka kaip juodraštis.',
    providerBody: '{system} neatsakė (bandymų: {n}).',
    viewerBody:
      '{run} praleido „{part}“: ši dalis skaito kiekvieno asmens {system}, o šis vykdymas neturi asmens, kurio vardu skaityti.',
    unknownBody: 'Nežinome, ar pavyko: {operation}. {system} to nepatvirtino.',
    review: 'Peržiūrėti',
    stopHere: 'Sustabdyti čia',
    stopConfirm: 'Sustabdyti čia? Paskutinė veikianti versija lieka kaip juodraštis.',
    stopYes: 'Sustabdyti',
    stopKeep: 'Tęsti',
    continueMore: 'Tęsti, skiriant dar {amount}',
    checkAgain: 'Tikrinti dar kartą',
    chooseRunAs: 'Pasirinkti, kieno vardu vykdoma',
    changeInChat: 'Keisti pokalbyje',
    shapeAdd:
      'Prideda {field} prie {table} · įrašai, gaunantys „{default}“: {n} · v{published} veikia toliau',
    shapeRename: 'Pervadina {old} į {new} · v{published} naudoja {old}, kol paskelbsite',
    shapeDropEditor:
      'Pašalina {field} iš įrašų (iš viso: {n}; su reikšme: {m}). Pirmiausia išsaugomas atkūrimo taškas.',
    shapeDropWaiting: 'Laukiama, kol redaktorius pašalins {field}. Patvirtinti gali: {names}.',
    shapeDropInUse:
      'Lauką {field} vis dar naudoja v{published}, kurią naudoja {audience}. Jis bus pašalintas, kai paskelbsite v{draft}.',
    shapeDropDone: 'Laukas {field} pašalintas iš juodraščio v{draft} · atkūrimo taškas {time}',
    shapeBusy: 'Kol kas nepavyko pakeisti lentelės {table}: ji užimta. Bandysime dar kartą {time}.',
    shapeUndo: 'Anuliuoti',
    shapeRemove: 'Pašalinti lauką',
    shapeKeep: 'Palikti lauką',
    shapeRestore: 'Atkurti',
    shapeTryNow: 'Bandyti dabar',
  },
  lv: {
    close: 'Aizvērt',
    cancel: 'Atcelt',
    dupTitle: 'Dublēt {app}',
    dupName: 'Nosaukums',
    dupNameDefault: '{app} kopija',
    dupWhat: 'Kas tiek pārņemts',
    dupCodeKey: 'Kods un iestatījumi',
    dupCodeValue: 'Viss no v{n} kā jauns privāts melnraksts',
    dupWhoKey: 'Kas var to atvērt',
    dupWhoValue: 'Tikai jūs, līdz to publicēsiet',
    dupConnKey: 'Savienojumi',
    dupConnValue:
      'Netiek kopēti. Kad pirmoreiz būs vajadzīgs, tā atkal lūgs atļauju lasīt: {systems}',
    dupHistoryKey: 'Vēsture',
    dupHistoryValue: 'Rindu vēsture, versijas un atjaunošanas punkti paliek lietotnē {app}',
    dupEntries: 'Ieraksti',
    dupEmpty: 'Sākt tukšu',
    dupEmptyHint: 'Tās pašas tabulas un lauki, bez ierakstiem.',
    dupCopy: 'Kopēt ierakstus tādus, kādi tie ir tagad',
    dupCopyPreview: 'Tabulas: {tables} · ieraksti: {entries} · faili: {files} · stāvoklis {time}',
    dupCopyUpdated: 'Atjaunināts · ieraksti tagad: {entries}',
    dupCopyLater: 'Vēlākas izmaiņas lietotnē {app} kopijā neparādīsies.',
    dupCopyUnavailable: 'Šai lietotnei ierakstu kopēšana nav pieejama: {reason}.',
    dupConfirm: 'Dublēt',
    dupBusy: 'Dublē…',
    dupDone: 'Izveidota kopija: {name}. Atvērt to varat tikai jūs.',
    dupUndo: 'Atsaukt',
    dupFailed: 'Neizdevās nokopēt ierakstus. Nekas netika izveidots.',
    dupUncertain:
      'Mēs nezinām, vai kopija tika izveidota. Pārbaudiet, pirms mēģināt vēlreiz, lai jums nebūtu divu.',
    tryAgain: 'Mēģināt vēlreiz',
    pausedPill: 'Pauzēts',
    stepOf: 'Solis {n} no {m}',
    nothingTwice: 'Nekas netiek darīts divreiz. Darbs turpinās no soļa „{step}”.',
    approvalBody: 'Gaida jūsu apstiprinājumu: {operation}.',
    approvalOthers: 'Gaida: {names}.',
    budgetBody:
      'Pauzēts, sasniedzot budžetu: {spent} no {limit}. Pēdējā strādājošā versija paliek kā melnraksts.',
    providerBody: '{system} neatbildēja (mēģinājumi: {n}).',
    viewerBody:
      '{run} izlaida „{part}”: šī daļa lasa katra cilvēka paša {system}, bet šai izpildei nav cilvēka, kura vārdā lasīt.',
    unknownBody: 'Mēs nezinām, vai izdevās: {operation}. {system} to neapstiprināja.',
    review: 'Pārskatīt',
    stopHere: 'Apturēt šeit',
    stopConfirm: 'Apturēt šeit? Pēdējā strādājošā versija paliek kā melnraksts.',
    stopYes: 'Apturēt',
    stopKeep: 'Turpināt',
    continueMore: 'Turpināt, piešķirot vēl {amount}',
    checkAgain: 'Pārbaudīt vēlreiz',
    chooseRunAs: 'Izvēlēties, kura vārdā izpildīt',
    changeInChat: 'Mainīt tērzēšanā',
    shapeAdd:
      'Pievieno {field} tabulai {table} · ieraksti, kas saņem „{default}”: {n} · v{published} turpina darboties',
    shapeRename: 'Pārdēvē {old} par {new} · v{published} izmanto {old}, līdz jūs publicēsiet',
    shapeDropEditor:
      'Noņem {field} no ierakstiem (kopā: {n}; ar vērtību: {m}). Vispirms tiek saglabāts atjaunošanas punkts.',
    shapeDropWaiting: 'Gaida, kad redaktors noņems {field}. Apstiprināt var: {names}.',
    shapeDropInUse:
      'Lauku {field} joprojām izmanto v{published}, ko izmanto {audience}. Tas tiks noņemts, kad publicēsiet v{draft}.',
    shapeDropDone: 'Lauks {field} noņemts no melnraksta v{draft} · atjaunošanas punkts {time}',
    shapeBusy: 'Vēl neizdevās mainīt tabulu {table}: tā ir aizņemta. Mēģināsim vēlreiz {time}.',
    shapeUndo: 'Atsaukt',
    shapeRemove: 'Noņemt lauku',
    shapeKeep: 'Paturēt lauku',
    shapeRestore: 'Atjaunot',
    shapeTryNow: 'Mēģināt tagad',
  },
  mt: {
    close: 'Agħlaq',
    cancel: 'Ikkanċella',
    dupTitle: 'Iddoppja {app}',
    dupName: 'Isem',
    dupNameDefault: 'Kopja ta’ {app}',
    dupWhat: 'X’jiġi magħha',
    dupCodeKey: 'Kodiċi u issettjar',
    dupCodeValue: 'Kollox f’v{n}, bħala abbozz privat ġdid',
    dupWhoKey: 'Min jista’ jiftaħha',
    dupWhoValue: 'Int biss, sakemm tippubblikaha',
    dupConnKey: 'Konnessjonijiet',
    dupConnValue:
      'Ma jiġux ikkupjati. Terġa’ titlob li taqra {systems} meta jkollha bżonnhom għall-ewwel darba',
    dupHistoryKey: 'Storja',
    dupHistoryValue:
      'L-istorja tar-ringieli, il-verżjonijiet u l-punti ta’ restawr jibqgħu ma’ {app}',
    dupEntries: 'Entrati',
    dupEmpty: 'Ibda vojt',
    dupEmptyHint: 'L-istess tabelli u oqsma, mingħajr entrati.',
    dupCopy: 'Ikkopja l-entrati kif inhuma issa',
    dupCopyPreview: 'Tabelli: {tables} · entrati: {entries} · fajls: {files} · sa {time}',
    dupCopyUpdated: 'Aġġornat · entrati issa: {entries}',
    dupCopyLater: 'Bidliet aktar tard f’{app} ma jaslux fil-kopja.',
    dupCopyUnavailable: 'L-ikkupjar tal-entrati mhuwiex disponibbli għal din l-app: {reason}.',
    dupConfirm: 'Iddoppja',
    dupBusy: 'Qed niddoppjaw…',
    dupDone: 'Saret il-kopja: {name}. Int biss tista’ tiftaħha.',
    dupUndo: 'Erġa’ lura',
    dupFailed: 'L-entrati ma setgħux jiġu kkupjati. Ma sar xejn.',
    dupUncertain:
      'Ma nafux jekk saritx il-kopja. Iċċekkja qabel terġa’ tipprova, biex ma jkollokx tnejn.',
    tryAgain: 'Erġa’ pprova',
    pausedPill: 'Fuq pawża',
    stepOf: 'Pass {n} minn {m}',
    nothingTwice: 'Xejn ma jsir darbtejn. Ikompli minn: {step}.',
    approvalBody: 'Qed nistennew l-OK tiegħek għal: {operation}.',
    approvalOthers: 'Qed nistennew lil: {names}.',
    budgetBody:
      'Fuq pawża mal-baġit: {spent} minn {limit}. L-aħħar verżjoni li taħdem tibqa’ bħala abbozz.',
    providerBody: 'Ma ġiet l-ebda tweġiba minn {system} (tentattivi: {n}).',
    viewerBody:
      'Il-parti “{part}” inqabżet f’{run}: taqra {system} ta’ kull persuna, u din it-tħaddim m’għandu l-ebda persuna li jaqra f’isimha.',
    unknownBody: 'Ma nafux jekk irnexxiex: {operation}. Ma waslet l-ebda konferma minn {system}.',
    review: 'Irrevedi',
    stopHere: 'Ieqaf hawn',
    stopConfirm: 'Tieqaf hawn? L-aħħar verżjoni li taħdem tibqa’ bħala abbozz.',
    stopYes: 'Ieqaf',
    stopKeep: 'Kompli',
    continueMore: 'Kompli b’{amount} aktar',
    checkAgain: 'Erġa’ ċċekkja',
    chooseRunAs: 'Agħżel f’isem min titħaddem',
    changeInChat: 'Ibdel fiċ-chat',
    shapeAdd:
      'Iżżid {field} ma’ {table} · entrati li jieħdu “{default}”: {n} · v{published} tibqa’ taħdem',
    shapeRename:
      'Tibdel l-isem ta’ {old} għal {new} · v{published} tibqa’ tuża {old} sakemm tippubblika',
    shapeDropEditor:
      'Tneħħi {field} mill-entrati (total: {n}; b’valur: {m}). L-ewwel jiġi ssejvjat punt ta’ restawr.',
    shapeDropWaiting: 'Qed nistennew li editur ineħħi {field}. Min jista’ jikkonferma: {names}.',
    shapeDropInUse:
      'Il-qasam {field} għadu jintuża minn v{published}, li tintuża minn {audience}. Jitneħħa meta tippubblika v{draft}.',
    shapeDropDone: 'Il-qasam {field} tneħħa mill-abbozz v{draft} · punt ta’ restawr {time}',
    shapeBusy:
      'It-tabella {table} għadha ma setgħetx tinbidel: hija okkupata. Nerġgħu nippruvaw fil-{time}.',
    shapeUndo: 'Erġa’ lura',
    shapeRemove: 'Neħħi l-qasam',
    shapeKeep: 'Żomm il-qasam',
    shapeRestore: 'Irrestawra',
    shapeTryNow: 'Ipprova issa',
  },
  nl: {
    close: 'Sluiten',
    cancel: 'Annuleren',
    dupTitle: '{app} dupliceren',
    dupName: 'Naam',
    dupNameDefault: '{app} (kopie)',
    dupWhat: 'Wat er meekomt',
    dupCodeKey: 'Code en instellingen',
    dupCodeValue: 'Alles uit v{n}, als nieuw privéconcept',
    dupWhoKey: 'Wie hem kan openen',
    dupWhoValue: 'Alleen jij, totdat je hem publiceert',
    dupConnKey: 'Verbindingen',
    dupConnValue:
      'Niet gekopieerd. De app vraagt opnieuw om {systems} te mogen lezen zodra hij dat voor het eerst nodig heeft',
    dupHistoryKey: 'Geschiedenis',
    dupHistoryValue: 'Rijgeschiedenis, versies en herstelpunten blijven bij {app}',
    dupEntries: 'Items',
    dupEmpty: 'Leeg beginnen',
    dupEmptyHint: 'Dezelfde tabellen en velden, geen items.',
    dupCopy: 'De items kopiëren zoals ze nu zijn',
    dupCopyPreview: '{tables} tabellen · {entries} items · {files} bestanden · om {time}',
    dupCopyUpdated: 'Bijgewerkt: nu {entries} items',
    dupCopyLater: 'Latere wijzigingen in {app} komen niet in de kopie terecht.',
    dupCopyUnavailable: 'Items kopiëren is niet beschikbaar voor deze app: {reason}.',
    dupConfirm: 'Dupliceren',
    dupBusy: 'Bezig met dupliceren…',
    dupDone: 'Kopie gemaakt: {name}. Alleen jij kunt hem openen.',
    dupUndo: 'Ongedaan maken',
    dupFailed: 'De items konden niet worden gekopieerd. Er is niets gemaakt.',
    dupUncertain:
      'We weten niet of de kopie is gemaakt. Controleer dit voordat je het opnieuw probeert, zodat je er geen twee krijgt.',
    tryAgain: 'Opnieuw proberen',
    pausedPill: 'Gepauzeerd',
    stepOf: 'Stap {n} van {m}',
    nothingTwice: 'Niets wordt twee keer gedaan. Het gaat verder vanaf {step}.',
    approvalBody: 'Wacht op jouw akkoord om {operation}.',
    approvalOthers: 'Wacht op {names}.',
    budgetBody:
      'Gepauzeerd bij het budget: {spent} van {limit}. De laatste werkende versie blijft als concept bewaard.',
    providerBody: '{system} heeft niet geantwoord ({n} pogingen).',
    viewerBody:
      '{run} heeft {part} overgeslagen: dat deel leest ieders eigen {system}-account, en deze uitvoering heeft niemand namens wie het kan lezen.',
    unknownBody: 'We weten niet of dit is gelukt: {operation}. {system} heeft het niet bevestigd.',
    review: 'Bekijken',
    stopHere: 'Hier stoppen',
    stopConfirm: 'Hier stoppen? De laatste werkende versie blijft als concept bewaard.',
    stopYes: 'Stoppen',
    stopKeep: 'Doorgaan',
    continueMore: 'Doorgaan met {amount} extra',
    checkAgain: 'Opnieuw controleren',
    chooseRunAs: 'Kies namens wie het draait',
    changeInChat: 'Wijzigen in chat',
    shapeAdd:
      'Voegt {field} toe aan {table} · {n} items krijgen “{default}” · v{published} blijft werken',
    shapeRename:
      'Hernoemt {old} naar {new} · v{published} blijft {old} gebruiken totdat je publiceert',
    shapeDropEditor:
      'Verwijdert {field} uit {n} items ({m} hebben een waarde). Eerst wordt een herstelpunt opgeslagen.',
    shapeDropWaiting:
      'Wacht tot iemand met bewerkrechten {field} verwijdert. Wie kan bevestigen: {names}.',
    shapeDropInUse:
      '{field} wordt nog gebruikt door v{published}, die {audience} gebruikt. Het veld wordt verwijderd zodra je v{draft} publiceert.',
    shapeDropDone: '{field} verwijderd uit concept v{draft} · herstelpunt {time}',
    shapeBusy: '{table} kon nog niet worden gewijzigd: de tabel is bezet. Nieuwe poging om {time}.',
    shapeUndo: 'Ongedaan maken',
    shapeRemove: 'Veld verwijderen',
    shapeKeep: 'Veld behouden',
    shapeRestore: 'Herstellen',
    shapeTryNow: 'Nu proberen',
  },
  pl: {
    close: 'Zamknij',
    cancel: 'Anuluj',
    dupTitle: 'Duplikuj aplikację {app}',
    dupName: 'Nazwa',
    dupNameDefault: '{app} – kopia',
    dupWhat: 'Co zostanie skopiowane',
    dupCodeKey: 'Kod i ustawienia',
    dupCodeValue: 'Wszystko z v{n} jako nowa prywatna wersja robocza',
    dupWhoKey: 'Kto może ją otworzyć',
    dupWhoValue: 'Tylko Ty, dopóki jej nie opublikujesz',
    dupConnKey: 'Połączenia',
    dupConnValue:
      'Nie są kopiowane. Gdy będą po raz pierwszy potrzebne, aplikacja ponownie poprosi o odczyt: {systems}',
    dupHistoryKey: 'Historia',
    dupHistoryValue: 'Historia wierszy, wersje i punkty przywracania zostają w aplikacji {app}',
    dupEntries: 'Wpisy',
    dupEmpty: 'Zacznij od zera',
    dupEmptyHint: 'Te same tabele i pola, bez wpisów.',
    dupCopy: 'Skopiuj wpisy w obecnym stanie',
    dupCopyPreview: 'Tabele: {tables} · wpisy: {entries} · pliki: {files} · stan na {time}',
    dupCopyUpdated: 'Zaktualizowano · wpisy teraz: {entries}',
    dupCopyLater: 'Późniejsze zmiany w aplikacji {app} nie trafią do kopii.',
    dupCopyUnavailable: 'Kopiowanie wpisów nie jest dostępne dla tej aplikacji: {reason}.',
    dupConfirm: 'Duplikuj',
    dupBusy: 'Duplikowanie…',
    dupDone: 'Utworzono: {name}. Tylko Ty możesz ją otworzyć.',
    dupUndo: 'Cofnij',
    dupFailed: 'Nie udało się skopiować wpisów. Nic nie zostało utworzone.',
    dupUncertain:
      'Nie wiemy, czy kopia została utworzona. Sprawdź przed ponowną próbą, aby nie powstały dwie.',
    tryAgain: 'Spróbuj ponownie',
    pausedPill: 'Wstrzymano',
    stepOf: 'Krok {n} z {m}',
    nothingTwice:
      'Nic nie jest wykonywane dwa razy. Działanie zostanie wznowione od kroku: {step}.',
    approvalBody: 'Czeka na Twoją zgodę: {operation}.',
    approvalOthers: 'Oczekiwanie na: {names}.',
    budgetBody:
      'Wstrzymano po osiągnięciu budżetu: {spent} z {limit}. Ostatnia działająca wersja pozostaje jako wersja robocza.',
    providerBody: 'System {system} nie odpowiedział (liczba prób: {n}).',
    viewerBody:
      'Uruchomienie {run} pominęło część: {part}. Odczytuje ona własny system {system} każdej osoby, a to uruchomienie nie działa w imieniu żadnej osoby.',
    unknownBody:
      'Nie wiemy, czy operacja została wykonana: {operation}. System {system} jej nie potwierdził.',
    review: 'Przejrzyj',
    stopHere: 'Zatrzymaj tutaj',
    stopConfirm: 'Zatrzymać tutaj? Ostatnia działająca wersja pozostaje jako wersja robocza.',
    stopYes: 'Zatrzymaj',
    stopKeep: 'Kontynuuj',
    continueMore: 'Kontynuuj z dodatkowym budżetem: {amount}',
    checkAgain: 'Sprawdź ponownie',
    chooseRunAs: 'Wybierz, w czyim imieniu działa',
    changeInChat: 'Zmień na czacie',
    shapeAdd:
      'Dodaje pole {field} do tabeli {table} · wpisy z wartością „{default}”: {n} · v{published} nadal działa',
    shapeRename: 'Zmienia nazwę {old} na {new} · v{published} używa {old}, dopóki nie opublikujesz',
    shapeDropEditor:
      'Usuwa pole {field} z wpisów (liczba: {n}; z wartością: {m}). Najpierw zapisywany jest punkt przywracania.',
    shapeDropWaiting: 'Czeka na usunięcie pola {field} przez edytora. Potwierdzić mogą: {names}.',
    shapeDropInUse:
      'Pole {field} jest nadal używane przez v{published}, z której korzysta {audience}. Zostanie usunięte, gdy opublikujesz v{draft}.',
    shapeDropDone: 'Usunięto pole {field} z wersji roboczej v{draft} · punkt przywracania: {time}',
    shapeBusy: 'Nie można jeszcze zmienić tabeli {table}: jest zajęta. Ponowna próba o {time}.',
    shapeUndo: 'Cofnij',
    shapeRemove: 'Usuń pole',
    shapeKeep: 'Zachowaj pole',
    shapeRestore: 'Przywróć',
    shapeTryNow: 'Spróbuj teraz',
  },
  pt: {
    close: 'Fechar',
    cancel: 'Cancelar',
    dupTitle: 'Duplicar {app}',
    dupName: 'Nome',
    dupNameDefault: '{app} (cópia)',
    dupWhat: 'O que é incluído',
    dupCodeKey: 'Código e definições',
    dupCodeValue: 'Tudo da v{n}, como novo rascunho privado',
    dupWhoKey: 'Quem a pode abrir',
    dupWhoValue: 'Só o utilizador, até a publicar',
    dupConnKey: 'Ligações',
    dupConnValue:
      'Não são copiadas. A app volta a pedir para ler {systems} quando precisar pela primeira vez',
    dupHistoryKey: 'Histórico',
    dupHistoryValue: 'O histórico de linhas, as versões e os pontos de restauro ficam em {app}',
    dupEntries: 'Registos',
    dupEmpty: 'Começar vazia',
    dupEmptyHint: 'As mesmas tabelas e campos, sem registos.',
    dupCopy: 'Copiar os registos tal como estão agora',
    dupCopyPreview: '{tables} tabelas · {entries} registos · {files} ficheiros · às {time}',
    dupCopyUpdated: 'Atualizado: agora {entries} registos',
    dupCopyLater: 'As alterações posteriores a {app} não chegam à cópia.',
    dupCopyUnavailable: 'Copiar registos não está disponível para esta app: {reason}.',
    dupConfirm: 'Duplicar',
    dupBusy: 'A duplicar…',
    dupDone: 'Cópia criada: {name}. Só o utilizador a pode abrir.',
    dupUndo: 'Anular',
    dupFailed: 'Não foi possível copiar os registos. Nada foi criado.',
    dupUncertain:
      'Não sabemos se a cópia foi criada. Verifique antes de tentar novamente, para não ficar com duas.',
    tryAgain: 'Tentar novamente',
    pausedPill: 'Em pausa',
    stepOf: 'Passo {n} de {m}',
    nothingTwice: 'Nada é feito duas vezes. Retoma a partir de {step}.',
    approvalBody: 'A aguardar a sua autorização para {operation}.',
    approvalOthers: 'A aguardar {names}.',
    budgetBody:
      'Em pausa por causa do orçamento: {spent} de {limit}. A última versão a funcionar fica como rascunho.',
    providerBody: '{system} não respondeu ({n} tentativas).',
    viewerBody:
      '{run} ignorou {part}: lê a conta de {system} de cada pessoa, e esta execução não tem nenhuma pessoa em nome de quem ler.',
    unknownBody:
      'Não sabemos se esta operação foi concluída: {operation}. {system} não a confirmou.',
    review: 'Rever',
    stopHere: 'Parar aqui',
    stopConfirm: 'Parar aqui? A última versão a funcionar fica como rascunho.',
    stopYes: 'Parar',
    stopKeep: 'Continuar',
    continueMore: 'Continuar com mais {amount}',
    checkAgain: 'Verificar novamente',
    chooseRunAs: 'Escolher em nome de quem é executada',
    changeInChat: 'Alterar no chat',
    shapeAdd:
      'Adiciona {field} a {table} · {n} registos recebem «{default}» · a v{published} continua a funcionar',
    shapeRename:
      'Muda o nome de {old} para {new} · a v{published} continua a usar {old} até publicar',
    shapeDropEditor:
      'Remove {field} de {n} registos ({m} têm um valor). Primeiro é guardado um ponto de restauro.',
    shapeDropWaiting:
      'A aguardar que alguém com permissão de edição remova {field}. Quem pode confirmar: {names}.',
    shapeDropInUse:
      'A v{published}, utilizada por {audience}, ainda usa {field}. O campo será removido quando publicar a v{draft}.',
    shapeDropDone: 'Campo {field} removido do rascunho v{draft} · ponto de restauro {time}',
    shapeBusy:
      'Ainda não foi possível alterar {table}: a tabela está ocupada. Nova tentativa às {time}.',
    shapeUndo: 'Anular',
    shapeRemove: 'Remover o campo',
    shapeKeep: 'Manter o campo',
    shapeRestore: 'Restaurar',
    shapeTryNow: 'Tentar agora',
  },
  ro: {
    close: 'Închideți',
    cancel: 'Renunțați',
    dupTitle: 'Duplicați {app}',
    dupName: 'Nume',
    dupNameDefault: 'Copie {app}',
    dupWhat: 'Ce se preia',
    dupCodeKey: 'Cod și setări',
    dupCodeValue: 'Tot ce există în v{n}, ca ciornă privată nouă',
    dupWhoKey: 'Cine o poate deschide',
    dupWhoValue: 'Doar dumneavoastră, până când o publicați',
    dupConnKey: 'Conexiuni',
    dupConnValue:
      'Nu se copiază. Va cere din nou permisiunea de citire când va avea nevoie prima dată de: {systems}',
    dupHistoryKey: 'Istoric',
    dupHistoryValue: 'Istoricul rândurilor, versiunile și punctele de restaurare rămân în {app}',
    dupEntries: 'Înregistrări',
    dupEmpty: 'Începeți fără date',
    dupEmptyHint: 'Aceleași tabele și câmpuri, fără înregistrări.',
    dupCopy: 'Copiați înregistrările așa cum sunt acum',
    dupCopyPreview: 'Tabele: {tables} · înregistrări: {entries} · fișiere: {files} · la {time}',
    dupCopyUpdated: 'Actualizat · înregistrări acum: {entries}',
    dupCopyLater: 'Modificările ulterioare din {app} nu ajung în copie.',
    dupCopyUnavailable:
      'Copierea înregistrărilor nu este disponibilă pentru această aplicație: {reason}.',
    dupConfirm: 'Duplicați',
    dupBusy: 'Se duplică…',
    dupDone: 'S-a creat {name}. Doar dumneavoastră o puteți deschide.',
    dupUndo: 'Anulați',
    dupFailed: 'Înregistrările nu au putut fi copiate. Nu s-a creat nimic.',
    dupUncertain:
      'Nu știm dacă s-a creat copia. Verificați înainte de a încerca din nou, ca să nu aveți două.',
    tryAgain: 'Încercați din nou',
    pausedPill: 'Întrerupt',
    stepOf: 'Pasul {n} din {m}',
    nothingTwice: 'Nimic nu se face de două ori. Se continuă de la pasul „{step}”.',
    approvalBody: 'Se așteaptă acordul dvs. pentru: {operation}.',
    approvalOthers: 'Se așteaptă: {names}.',
    budgetBody:
      'Întrerupt la limita bugetului: {spent} din {limit}. Ultima versiune funcțională rămâne ca ciornă.',
    providerBody: '{system} nu a răspuns (încercări: {n}).',
    viewerBody:
      '{run} a omis „{part}”: aceasta citește contul {system} propriu al fiecărei persoane, iar această rulare nu are o persoană în numele căreia să citească.',
    unknownBody: 'Nu știm dacă operațiunea s-a efectuat: {operation}. {system} nu a confirmat.',
    review: 'Examinați',
    stopHere: 'Opriți aici',
    stopConfirm: 'Opriți aici? Ultima versiune funcțională rămâne ca ciornă.',
    stopYes: 'Opriți',
    stopKeep: 'Continuați',
    continueMore: 'Continuați cu încă {amount}',
    checkAgain: 'Verificați din nou',
    chooseRunAs: 'Alegeți în numele cui rulează',
    changeInChat: 'Modificați în chat',
    shapeAdd:
      'Adaugă {field} în {table} · înregistrări care primesc „{default}”: {n} · v{published} funcționează în continuare',
    shapeRename: 'Redenumește {old} în {new} · v{published} folosește {old} până când publicați',
    shapeDropEditor:
      'Elimină {field} din înregistrări (total: {n}; cu valoare: {m}). Mai întâi se salvează un punct de restaurare.',
    shapeDropWaiting: 'Se așteaptă ca un editor să elimine {field}. Pot confirma: {names}.',
    shapeDropInUse:
      'Câmpul {field} este încă folosit de v{published}, pe care o folosește {audience}. Este eliminat când publicați v{draft}.',
    shapeDropDone:
      'Câmpul {field} a fost eliminat din ciorna v{draft} · punct de restaurare {time}',
    shapeBusy:
      'Tabelul {table} nu a putut fi modificat încă: este ocupat. Încercăm din nou la {time}.',
    shapeUndo: 'Anulați',
    shapeRemove: 'Eliminați câmpul',
    shapeKeep: 'Păstrați câmpul',
    shapeRestore: 'Restaurați',
    shapeTryNow: 'Încercați acum',
  },
  sk: {
    close: 'Zavrieť',
    cancel: 'Zrušiť',
    dupTitle: 'Duplikovať aplikáciu {app}',
    dupName: 'Názov',
    dupNameDefault: '{app} – kópia',
    dupWhat: 'Čo sa skopíruje',
    dupCodeKey: 'Kód a nastavenia',
    dupCodeValue: 'Všetko z v{n} ako nový súkromný koncept',
    dupWhoKey: 'Kto ju môže otvoriť',
    dupWhoValue: 'Len vy, kým ju nepublikujete',
    dupConnKey: 'Pripojenia',
    dupConnValue:
      'Nekopírujú sa. Keď ich bude aplikácia prvýkrát potrebovať, znova požiada o čítanie: {systems}',
    dupHistoryKey: 'História',
    dupHistoryValue: 'História riadkov, verzie a body obnovenia zostávajú v aplikácii {app}',
    dupEntries: 'Záznamy',
    dupEmpty: 'Začať naprázdno',
    dupEmptyHint: 'Rovnaké tabuľky a polia, žiadne záznamy.',
    dupCopy: 'Skopírovať záznamy v aktuálnom stave',
    dupCopyPreview: 'Tabuľky: {tables} · záznamy: {entries} · súbory: {files} · stav k {time}',
    dupCopyUpdated: 'Aktualizované · záznamy teraz: {entries}',
    dupCopyLater: 'Neskoršie zmeny v aplikácii {app} sa do kópie neprenesú.',
    dupCopyUnavailable: 'Kopírovanie záznamov nie je pre túto aplikáciu dostupné: {reason}.',
    dupConfirm: 'Duplikovať',
    dupBusy: 'Duplikuje sa…',
    dupDone: 'Vytvorené: {name}. Otvoriť ju môžete len vy.',
    dupUndo: 'Vrátiť späť',
    dupFailed: 'Záznamy sa nepodarilo skopírovať. Nič sa nevytvorilo.',
    dupUncertain:
      'Nevieme, či sa kópia vytvorila. Pred ďalším pokusom to skontrolujte, aby ste nemali dve.',
    tryAgain: 'Skúsiť znova',
    pausedPill: 'Pozastavené',
    stepOf: 'Krok {n} z {m}',
    nothingTwice: 'Nič sa nevykoná dvakrát. Pokračuje sa od kroku: {step}.',
    approvalBody: 'Čaká sa na váš súhlas: {operation}.',
    approvalOthers: 'Čaká sa na: {names}.',
    budgetBody:
      'Pozastavené na limite rozpočtu: {spent} z {limit}. Posledná funkčná verzia zostane ako koncept.',
    providerBody: 'Systém {system} neodpovedal (počet pokusov: {n}).',
    viewerBody:
      'Spustenie {run} preskočilo časť: {part}. Tá číta vlastný systém {system} každej osoby a toto spustenie nebeží za žiadnu osobu.',
    unknownBody: 'Nevieme, či bola operácia vykonaná: {operation}. Systém {system} ju nepotvrdil.',
    review: 'Prezrieť',
    stopHere: 'Zastaviť tu',
    stopConfirm: 'Zastaviť tu? Posledná funkčná verzia zostane ako koncept.',
    stopYes: 'Zastaviť',
    stopKeep: 'Pokračovať',
    continueMore: 'Pokračovať s ďalším rozpočtom: {amount}',
    checkAgain: 'Skontrolovať znova',
    chooseRunAs: 'Vybrať, za koho sa spúšťa',
    changeInChat: 'Zmeniť v chate',
    shapeAdd:
      'Pridá pole {field} do tabuľky {table} · záznamy s hodnotou „{default}“: {n} · v{published} naďalej funguje',
    shapeRename: 'Premenuje {old} na {new} · v{published} používa {old}, kým nepublikujete',
    shapeDropEditor:
      'Odstráni pole {field} zo záznamov (počet: {n}; s hodnotou: {m}). Najprv sa uloží bod obnovenia.',
    shapeDropWaiting: 'Čaká sa, kým editor odstráni pole {field}. Potvrdiť môžu: {names}.',
    shapeDropInUse:
      'Pole {field} stále používa v{published}, ktorú používa {audience}. Odstráni sa, keď publikujete v{draft}.',
    shapeDropDone: 'Pole {field} odstránené z konceptu v{draft} · bod obnovenia: {time}',
    shapeBusy: 'Tabuľku {table} zatiaľ nemožno zmeniť: je zaneprázdnená. Ďalší pokus o {time}.',
    shapeUndo: 'Vrátiť späť',
    shapeRemove: 'Odstrániť pole',
    shapeKeep: 'Ponechať pole',
    shapeRestore: 'Obnoviť',
    shapeTryNow: 'Skúsiť teraz',
  },
  sl: {
    close: 'Zapri',
    cancel: 'Prekliči',
    dupTitle: 'Podvoji aplikacijo {app}',
    dupName: 'Ime',
    dupNameDefault: '{app} – kopija',
    dupWhat: 'Kaj se prenese',
    dupCodeKey: 'Koda in nastavitve',
    dupCodeValue: 'Vse iz v{n} kot nov zasebni osnutek',
    dupWhoKey: 'Kdo jo lahko odpre',
    dupWhoValue: 'Samo vi, dokler je ne objavite',
    dupConnKey: 'Povezave',
    dupConnValue:
      'Ne kopirajo se. Ko jih bo aplikacija prvič potrebovala, bo znova prosila za branje: {systems}',
    dupHistoryKey: 'Zgodovina',
    dupHistoryValue: 'Zgodovina vrstic, različice in točke obnovitve ostanejo v aplikaciji {app}',
    dupEntries: 'Vnosi',
    dupEmpty: 'Začni prazno',
    dupEmptyHint: 'Enake tabele in polja, brez vnosov.',
    dupCopy: 'Kopiraj vnose v trenutnem stanju',
    dupCopyPreview: 'Tabele: {tables} · vnosi: {entries} · datoteke: {files} · stanje ob {time}',
    dupCopyUpdated: 'Posodobljeno · vnosi zdaj: {entries}',
    dupCopyLater: 'Poznejše spremembe v aplikaciji {app} se ne prenesejo v kopijo.',
    dupCopyUnavailable: 'Kopiranje vnosov za to aplikacijo ni na voljo: {reason}.',
    dupConfirm: 'Podvoji',
    dupBusy: 'Podvajanje…',
    dupDone: 'Ustvarjeno: {name}. Odprete jo lahko samo vi.',
    dupUndo: 'Razveljavi',
    dupFailed: 'Vnosov ni bilo mogoče kopirati. Nič ni bilo ustvarjeno.',
    dupUncertain:
      'Ne vemo, ali je bila kopija ustvarjena. Pred ponovnim poskusom preverite, da ne boste imeli dveh.',
    tryAgain: 'Poskusi znova',
    pausedPill: 'Zaustavljeno',
    stepOf: 'Korak {n} od {m}',
    nothingTwice: 'Nič se ne izvede dvakrat. Nadaljuje se od koraka: {step}.',
    approvalBody: 'Čaka na vašo odobritev: {operation}.',
    approvalOthers: 'Čakanje na: {names}.',
    budgetBody:
      'Zaustavljeno ob proračunu: {spent} od {limit}. Zadnja delujoča različica ostane kot osnutek.',
    providerBody: 'Sistem {system} ni odgovoril (število poskusov: {n}).',
    viewerBody:
      'Zagon {run} je preskočil del: {part}. Ta bere lasten sistem {system} vsake osebe, ta zagon pa se ne izvaja v imenu nobene osebe.',
    unknownBody:
      'Ne vemo, ali je bila operacija izvedena: {operation}. Sistem {system} je ni potrdil.',
    review: 'Preglej',
    stopHere: 'Ustavi tukaj',
    stopConfirm: 'Ustaviti tukaj? Zadnja delujoča različica ostane kot osnutek.',
    stopYes: 'Ustavi',
    stopKeep: 'Nadaljuj',
    continueMore: 'Nadaljuj z dodatnim proračunom: {amount}',
    checkAgain: 'Preveri znova',
    chooseRunAs: 'Izberi, v imenu koga se izvaja',
    changeInChat: 'Spremeni v klepetu',
    shapeAdd:
      'Doda polje {field} v tabelo {table} · vnosi z vrednostjo „{default}“: {n} · v{published} še naprej deluje',
    shapeRename: 'Preimenuje {old} v {new} · v{published} uporablja {old}, dokler ne objavite',
    shapeDropEditor:
      'Odstrani polje {field} iz vnosov (število: {n}; z vrednostjo: {m}). Najprej se shrani točka obnovitve.',
    shapeDropWaiting: 'Čaka, da urednik odstrani polje {field}. Potrdijo lahko: {names}.',
    shapeDropInUse:
      'Polje {field} še vedno uporablja v{published}, ki jo uporablja {audience}. Odstranjeno bo, ko objavite v{draft}.',
    shapeDropDone: 'Polje {field} odstranjeno iz osnutka v{draft} · točka obnovitve: {time}',
    shapeBusy: 'Tabele {table} še ni mogoče spremeniti: je zasedena. Ponoven poskus ob {time}.',
    shapeUndo: 'Razveljavi',
    shapeRemove: 'Odstrani polje',
    shapeKeep: 'Obdrži polje',
    shapeRestore: 'Obnovi',
    shapeTryNow: 'Poskusi zdaj',
  },
};

export const appOperationsCopy: NamespaceCatalog<AppOperationsCopyKey> = {
  en,
  bg: translated.bg,
  cs: translated.cs,
  da: translated.da,
  de: translated.de,
  el: translated.el,
  es: translated.es,
  et: translated.et,
  fi: translated.fi,
  fr: translated.fr,
  ga: translated.ga,
  hr: translated.hr,
  hu: translated.hu,
  it: translated.it,
  lt: translated.lt,
  lv: translated.lv,
  mt: translated.mt,
  nl: translated.nl,
  pl: translated.pl,
  pt: translated.pt,
  ro: translated.ro,
  sk: translated.sk,
  sl: translated.sl,
  sv,
};
export default appOperationsCopy;
